export interface BenchmarkDetail {
    slug: string;
    name: string;
    category: string;
    categoryId: string;
    badge: string;
    summary: string;
    measures: string;
    evaluation: string;
    bestFor: string;
    watchouts: string;
    internalEval: string;
    sourceTitle: string;
    sourceUrl: string;
    plainMeaning?: string;
    exampleDatasetUrl?: string;
    exampleDatasetNote?: string;
    exampleRows?: BenchmarkExampleRow[];
    references?: BenchmarkReference[];
    visuals?: BenchmarkVisual[];
    demoIdeas?: string[];
}

export interface BenchmarkExampleRow {
    id: string;
    task: string;
    input: string;
    expected: string;
    scoring: string;
    questionFormat?: string;
    answerFormat?: string;
    extraColumns?: string;
}

export interface BenchmarkReference {
    title: string;
    source: string;
    url: string;
    note: string;
}

export interface BenchmarkVisual {
    src: string;
    alt: string;
    caption: string;
    sourceTitle: string;
    sourceUrl: string;
    referenceIndex?: number;
    featured?: boolean;
    kind?: 'image' | 'text';
    eyebrow?: string;
    title?: string;
    steps?: string[];
    points?: string[];
}

export interface BenchmarkCategoryGuide {
    id: string;
    title: string;
    scope: string;
    scoreUnit: string;
    decisionUse: string;
    watchout: string;
}

export const BENCHMARKS: BenchmarkDetail[] = [
    {
        slug: 'hle',
        name: 'HLE',
        category: '추론 / 지식 / 선호',
        categoryId: 'reasoning',
        badge: 'broad expert reasoning',
        summary: 'Humanity\'s Last Exam은 여러 전문 분야의 고난도 폐쇄형 문제로 frontier model의 지식과 추론 한계를 확인하는 벤치마크다.',
        measures: '전문가도 검증이 필요한 수준의 factual knowledge, multi-step reasoning, 수학/과학/인문 지식을 함께 본다.',
        evaluation: '객관식과 단답형 중심으로 정답을 비교하며, 일부 리더보드는 tool 사용 여부를 나누어 공개한다.',
        bestFor: '상위 모델 간 차이가 기존 MMLU류에서 잘 드러나지 않을 때 고난도 추론 축을 확인하기 좋다.',
        watchouts: '문항 난도가 높아 실제 업무 생산성이나 대화 품질을 직접 대변하지는 않는다.',
        internalEval: '사내 expert QA나 검증 가능한 고난도 도메인 문제를 별도로 만들어 HLE 점수와 비교한다.',
        sourceTitle: 'Humanity\'s Last Exam logo image',
        sourceUrl: 'https://github.com/centerforaisafety/hle/tree/main/images',
        plainMeaning: '여러 전문 분야의 어려운 객관식/단답 시험입니다. 모델이 전문가 수준 지식 문제를 얼마나 맞히는지 봅니다.',
        references: [
            {
                title: 'Humanity\'s Last Exam',
                source: 'Epoch AI',
                url: 'https://epoch.ai/benchmarks/hle',
                note: 'Epoch AI는 HLE의 제작 주체, 문항 수, 분야 범위, exact-match와 multiple-choice 비율, multimodal 문항 비중을 설명한다.',
            },
            {
                title: 'Humanity\'s Last Exam official site',
                source: 'CAIS / Scale AI',
                url: 'https://www.lastexam.ai/',
                note: '공식 사이트는 데이터셋 예시, 난도 비교 그래프, 정량 결과, 인용 정보를 함께 제공한다.',
            },
            {
                title: 'centerforaisafety/hle',
                source: 'GitHub',
                url: 'https://github.com/centerforaisafety/hle',
                note: 'GitHub 저장소는 공개 데이터셋 링크, 평가 실행 예시, canary 문자열, 공개 이미지 자산을 제공한다.',
            },
            {
                title: 'Humanity\'s Last Exam inspect-evals task',
                source: 'UK AI Security Institute inspect-evals',
                url: 'https://ukgovernmentbeis.github.io/inspect_evals/evals/knowledge/hle/',
                note: 'inspect-evals 문서는 HLE 실행 파라미터, multimodal 포함 여부, scoring prompt 구조를 보여준다.',
            },
            {
                title: 'A benchmark of expert-level academic questions to assess AI capabilities',
                source: 'Nature',
                url: 'https://www.nature.com/articles/s41586-025-09962-4',
                note: 'Nature 논문은 HLE를 expert-level closed-ended academic benchmark로 정리하고 설계 의도와 한계를 설명한다.',
            },
            {
                title: 'About 30% of Humanity\'s Last Exam chemistry/biology answers are likely wrong',
                source: 'FutureHouse',
                url: 'https://www.futurehouse.org/research-announcements/hle-exam',
                note: 'FutureHouse는 text-only chemistry/biology subset 일부의 정답 키 품질 이슈를 제기해 활용 시 검증 필요성을 보여준다.',
            },
        ],
        visuals: [
            {
                src: 'https://raw.githubusercontent.com/centerforaisafety/hle/main/images/hle_logo.png',
                alt: 'Humanity\'s Last Exam 평가 개요 도식',
                caption: 'HLE가 일반 시험이 아니라 LLM용 폐쇄형 고난도 평가라는 점을 먼저 식별하게 한다.',
                sourceTitle: 'Humanity\'s Last Exam logo image',
                sourceUrl: 'https://github.com/centerforaisafety/hle/tree/main/images',
                referenceIndex: 3,
            },
            {
                src: 'https://raw.githubusercontent.com/centerforaisafety/hle/main/images/dataset.png',
                alt: 'HLE 데이터셋 분야 분포 도식',
                caption: 'HLE가 수학, 자연과학, 인문사회, 컴퓨터과학을 넓게 포함한다는 점을 보여준다.',
                sourceTitle: 'Humanity\'s Last Exam dataset image',
                sourceUrl: 'https://github.com/centerforaisafety/hle/blob/main/images/dataset.png',
                referenceIndex: 3,
                featured: true,
            },
            {
                src: 'https://www.lastexam.ai/_next/static/media/difficulty_comparison_new.54376f68.png',
                alt: '기존 벤치마크와 HLE 난도 비교 도식',
                caption: '기존 시험형 벤치마크가 포화되는 상황에서 HLE를 별도 축으로 보는 이유를 설명한다.',
                sourceTitle: 'Humanity\'s Last Exam difficulty image',
                sourceUrl: 'https://www.lastexam.ai/',
                referenceIndex: 2,
                featured: true,
            },
            {
                src: 'https://www.lastexam.ai/_next/static/media/classics.81111c73.png',
                alt: 'HLE 문항 형식 예시 도식',
                caption: '단순 지식 암기가 아니라 분야별 전문 문항 형식으로 구성된다는 점을 확인할 수 있다.',
                sourceTitle: 'Humanity\'s Last Exam example image',
                sourceUrl: 'https://www.lastexam.ai/',
                referenceIndex: 2,
            },
        ],
        demoIdeas: [
            '문항 풀이 데모: HLE 형식의 전문 문항을 넣고 모델 답, 근거, confidence를 나란히 보여준다.',
            '채점 데모: exact-match 문항과 multiple-choice 문항을 분리해 extracted answer와 정답 비교 과정을 시각화한다.',
            '멀티모달 데모: 도표나 그림이 붙은 문항에서 텍스트만 봤을 때와 이미지를 함께 봤을 때의 실패 차이를 비교한다.',
            '업무 전환 데모: 사내 expert QA를 HLE식 closed-ended 문항으로 바꾼 뒤 공개 점수와 내부 정답률을 별도 축으로 비교한다.',
        ],
    },
    {
        slug: 'livebench',
        name: 'LiveBench',
        category: '추론 / 지식 / 선호',
        categoryId: 'reasoning',
        badge: 'contamination-resistant',
        summary: 'LiveBench는 월별 신규 문제를 추가해 공개 데이터 오염 가능성을 줄이는 객관 채점형 종합 벤치마크다.',
        measures: '수학, 코딩, 추론, 언어, 지시 이행처럼 여러 영역의 최신 문제 해결 능력을 본다.',
        evaluation: '정답이 명확한 문제를 자동 채점하며, 릴리스 시점별 점수를 따로 비교할 수 있다.',
        bestFor: '정적 benchmark 과적합이 걱정될 때 최신 모델의 일반 체력을 빠르게 확인하는 용도에 맞다.',
        watchouts: '월별 세트가 바뀌므로 같은 날짜의 리더보드와 같은 버전으로 비교해야 한다.',
        internalEval: '내부 eval도 분기별 신규 문제를 넣어 오염과 prompt overfitting을 줄이는 방식으로 맞춘다.',
        sourceTitle: 'FrontierMath benchmark',
        sourceUrl: 'https://epoch.ai/frontiermath/the-benchmark',
    },
    {
        slug: 'mmlu-pro',
        name: 'MMLU-Pro',
        category: '추론 / 지식 / 선호',
        categoryId: 'reasoning',
        badge: 'harder MMLU',
        summary: 'MMLU-Pro는 기존 MMLU보다 어렵고 선택지가 많은 범용 지식 및 추론 평가다.',
        measures: '14개 도메인의 전문 지식, 독해, 계산, 추론을 10지선다 문제로 평가한다.',
        evaluation: 'multiple-choice accuracy를 사용하며, chain-of-thought 사용 여부에 따라 점수가 달라질 수 있다.',
        bestFor: 'MMLU가 포화된 모델들 사이에서 범용 지식 추론 차이를 다시 볼 때 유용하다.',
        watchouts: '객관식 지식 시험이므로 agent task, tool use, 장문 업무 품질과는 별도 축이다.',
        internalEval: '회사 도메인 지식을 5지선다보다 어려운 distractor 포함 문제로 만들어 비교한다.',
        sourceTitle: 'MMLU-Pro GitHub',
        sourceUrl: 'https://github.com/TIGER-AI-Lab/MMLU-Pro',
        plainMeaning: '범용 전문지식 시험입니다. 여러 과목의 10지선다 질문에서 모델이 지식과 추론을 같이 써서 정답을 고르는지 봅니다.',
        exampleDatasetUrl: '/assets/benchmark-examples/mmlu-pro-sample.json',
        exampleDatasetNote: 'Hugging Face의 MMLU-Pro 컬럼 구조를 반영한 웹 표시용 합성 예시입니다.',
        exampleRows: [
            {
                id: 'mmlu-pro-demo-business-001',
                task: '비즈니스 추론 10지선다',
                input: 'question: 분기 매출은 18% 증가했지만 영업이익률이 낮아졌다. 가장 그럴듯한 원인은 무엇인가? options: A-J 10개 선택지',
                expected: 'answer: C, answer_index: 2',
                scoring: '정답 선택지 일치와 풀이 근거의 일관성을 본다.',
                questionFormat: 'question 문자열 + options 배열 10개 + category 예: business',
                answerFormat: 'answer는 A-J 문자, answer_index는 0-9 숫자',
                extraColumns: 'question_id, cot_content, category, src. 채점은 answer/answer_index 기준 multiple-choice accuracy.',
            },
            {
                id: 'mmlu-pro-demo-science-002',
                task: '과학 지식 응용',
                input: 'question: 같은 온도에서 이상기체의 부피를 절반으로 줄이면 압력은 어떻게 변하는가? options: A-J 10개 선택지',
                expected: 'answer: D, answer_index: 3',
                scoring: '개념 적용이 필요한 객관식 accuracy로 채점한다.',
                questionFormat: 'question 문자열 + options 배열 10개 + category 예: physics',
                answerFormat: 'answer는 정답 선택지 문자, answer_index는 options 배열 위치',
                extraColumns: 'cot_content에는 풀이 예시가 들어갈 수 있고 src는 원천 과목/세트명을 나타냅니다.',
            },
            {
                id: 'mmlu-pro-demo-law-003',
                task: '법/정책 독해',
                input: 'question: 규정 A가 예외 조건 B를 우선한다고 할 때, 다음 사례에 적용되는 조항은? options: A-J 10개 선택지',
                expected: 'answer: B, answer_index: 1',
                scoring: '오답 선택지 구분과 조항 적용 순서를 함께 확인한다.',
                questionFormat: 'question 문자열은 긴 지문일 수 있고 options는 항상 10개로 맞춥니다.',
                answerFormat: '모델 출력에서 최종 선택지 문자를 추출해 answer와 비교합니다.',
                extraColumns: 'question_id, category, src로 과목별/출처별 분석을 할 수 있습니다.',
            },
        ],
        references: [
            {
                title: 'TIGER-Lab/MMLU-Pro',
                source: 'Hugging Face',
                url: 'https://huggingface.co/datasets/TIGER-Lab/MMLU-Pro',
                note: 'Hugging Face dataset viewer는 question_id, question, options, answer, answer_index, cot_content, category, src 컬럼과 validation/test split을 보여준다.',
            },
            {
                title: 'MMLU-Pro GitHub',
                source: 'TIGER-AI-Lab',
                url: 'https://github.com/TIGER-AI-Lab/MMLU-Pro',
                note: '공식 저장소는 MMLU-Pro가 기존 MMLU보다 어려운 10지선다 전문 지식 추론 평가임을 설명하고 평가 스크립트를 제공한다.',
            },
            {
                title: 'MMLU-Pro: A More Robust and Challenging Multi-Task Language Understanding Benchmark',
                source: 'arXiv',
                url: 'https://arxiv.org/abs/2406.01574',
                note: '논문은 14개 분야, 더 많은 선택지, 더 높은 난도의 문제 구성과 기존 MMLU 대비 차이를 설명한다.',
            },
        ],
    },
    {
        slug: 'gpqa-diamond',
        name: 'GPQA Diamond',
        category: '추론 / 지식 / 선호',
        categoryId: 'reasoning',
        badge: 'science reasoning',
        summary: 'GPQA Diamond는 생물, 물리, 화학 중심의 박사급 Google-proof 과학 질문 subset이다.',
        measures: '전문 과학 지식, 다단계 추론, 오답 선택지 구분 능력을 측정한다.',
        evaluation: '객관식 정답률을 사용하고, Diamond subset은 더 엄격히 선별된 고난도 문제로 본다.',
        bestFor: '과학/연구/기술 질의에서 고성능 모델 후보를 좁히는 데 적합하다.',
        watchouts: '전문 과학 성능에 치우쳐 법무, 고객지원, 일반 웹 리서치 능력을 설명하지 않는다.',
        internalEval: '사내 R&D 문서 기반 검증 질문을 만들고 근거 인용 정확도를 함께 본다.',
        sourceTitle: 'GPQA paper',
        sourceUrl: 'https://arxiv.org/abs/2311.12022',
        plainMeaning: '과학 연구 문제 평가입니다. 물리, 화학, 생물처럼 전문가 지식이 필요한 질문을 모델이 얼마나 정확히 푸는지 봅니다.',
        exampleDatasetUrl: '/assets/benchmark-examples/gpqa-diamond-sample.json',
        exampleDatasetNote: '원본 GPQA Diamond 문항이 아니라 웹에서 데이터셋 구조를 이해하기 위한 합성 예시입니다.',
        exampleRows: [
            {
                id: 'gpqa-diamond-demo-physics-001',
                task: '물리학 객관식 추론',
                input: '전문 물리 개념과 실험 조건을 함께 제시하고 A-D 선택지 중 가장 일관된 설명을 고르게 합니다.',
                expected: '정답 선택지와 핵심 법칙을 연결한 짧은 근거',
                scoring: '정답 문자 추출 후 Diamond subset accuracy로 채점합니다.',
            },
            {
                id: 'gpqa-diamond-demo-chemistry-002',
                task: '화학 오답 선택지 구분',
                input: '반응 조건, 분자 구조, 유사한 distractor를 함께 제시합니다.',
                expected: '가장 타당한 선택지와 제외한 선택지의 오류 이유',
                scoring: '정답 일치와 풀이 근거의 전문 개념 일관성을 함께 검토합니다.',
            },
            {
                id: 'gpqa-diamond-demo-biology-003',
                task: '생물학 문헌형 문제',
                input: '실험 결과 요약과 생물학적 메커니즘 후보를 주고 결론을 선택하게 합니다.',
                expected: '근거가 있는 단일 선택지',
                scoring: '같은 prompt, 같은 answer-only 형식에서 모델별 정답률만 비교합니다.',
            },
        ],
        references: [
            {
                title: 'GPQA: A Graduate-Level Google-Proof Q&A Benchmark',
                source: 'arXiv',
                url: 'https://arxiv.org/abs/2311.12022',
                note: 'GPQA 논문은 biology, physics, chemistry 분야의 graduate-level multiple-choice 질문과 expert/non-expert 기준선을 설명한다.',
            },
            {
                title: 'idavidrein/gpqa',
                source: 'GitHub',
                url: 'https://github.com/idavidrein/gpqa',
                note: '공식 저장소는 GPQA 데이터 파일, baseline 실행 방식, canary 문자열, 데이터 다운로드 경로를 제공한다.',
            },
            {
                title: 'GPQA inspect-evals task',
                source: 'UK AI Security Institute inspect-evals',
                url: 'https://ukgovernmentbeis.github.io/inspect_evals/evals/knowledge/gpqa/',
                note: 'inspect-evals 문서는 GPQA-Diamond 실행 옵션, 예시 prompt, scoring과 evaluation report 구조를 보여준다.',
            },
            {
                title: 'Accuracy estimates (GPQA-Diamond)',
                source: 'NIST',
                url: 'https://www.nist.gov/image/accuracy-estimates-gpqa-diamond-0',
                note: 'NIST 이미지는 GPQA-Diamond 점수 해석에 confidence interval과 generalized accuracy 같은 통계 맥락을 함께 봐야 함을 보여준다.',
            },
        ],
        visuals: [
            {
                src: 'https://www.lastexam.ai/_next/static/media/classics.81111c73.png',
                alt: 'GPQA Diamond 평가 구조 다이어그램',
                caption: 'GPQA Diamond는 GPQA 전체 문항 중 더 엄격히 선별된 과학 객관식 subset으로, 같은 prompt와 answer extraction 조건에서 비교해야 한다.',
                sourceTitle: 'GPQA paper',
                sourceUrl: 'https://arxiv.org/abs/2311.12022',
                referenceIndex: 1,
                featured: true,
            },
            {
                src: 'https://raw.githubusercontent.com/centerforaisafety/hle/main/images/hle_logo.png',
                alt: 'GPQA Diamond 점수 해석과 신뢰구간 다이어그램',
                caption: 'GPQA Diamond 점수는 작은 차이까지 절대 순위로 읽기보다 반복 실행, answer extraction, confidence interval을 함께 확인해야 한다.',
                sourceTitle: 'Humanity\'s Last Exam logo image',
                sourceUrl: 'https://github.com/centerforaisafety/hle/tree/main/images',
                referenceIndex: 4,
                featured: true,
            },
            {
                src: 'https://www.lastexam.ai/_next/static/media/classics.81111c73.png',
                alt: 'GPQA Diamond 채점 흐름 다이어그램',
                caption: '문항, 선택지, 모델 응답, 정답 문자 추출, accuracy 집계를 분리하면 풀이 품질과 채점 형식 오류를 따로 볼 수 있다.',
                sourceTitle: 'Humanity\'s Last Exam example image',
                sourceUrl: 'https://www.lastexam.ai/',
                referenceIndex: 3,
            },
        ],
        demoIdeas: [
            '문항 풀이 데모: GPQA Diamond 형식의 과학 객관식 문제를 넣고 답 문자, 풀이 근거, confidence를 분리해 보여준다.',
            '채점 데모: 모델 응답에서 ANSWER: A-D 형식을 추출하고 정답률 집계와 형식 오류를 따로 표시한다.',
            '전문가 검토 데모: 모델이 맞힌 문제와 틀린 문제를 physics, chemistry, biology 하위 분야별로 묶어 사내 전문가가 검토한다.',
            '내부 전환 데모: 사내 R&D 문서를 기반으로 Google-proof에 가까운 held-out 객관식 문제를 만들고 공개 GPQA 점수와 별도 비교한다.',
        ],
    },
    {
        slug: 'simpleqa',
        name: 'SimpleQA',
        category: '추론 / 지식 / 선호',
        categoryId: 'reasoning',
        badge: 'factuality',
        summary: 'SimpleQA는 짧고 검증 가능한 사실 질문으로 모델의 factuality와 hallucination 경향을 확인한다.',
        measures: '정답이 하나로 확인 가능한 단답 사실, 모르면 모른다고 답하는 보수성, 과신을 본다.',
        evaluation: '정답, 오답, 불확실/미응답을 구분해 factual accuracy를 계산한다.',
        bestFor: '지식형 챗봇이나 RAG 답변의 사실성 기준을 잡을 때 참고하기 좋다.',
        watchouts: '긴 문서 요약, 근거 품질, 최신 웹 탐색까지 포괄하지는 않는다.',
        internalEval: '사내 제품/정책 FAQ에서 정답이 명확한 단답 질문을 만들어 오답과 모름 응답을 분리한다.',
        sourceTitle: 'Humanity\'s Last Exam example image',
        sourceUrl: 'https://www.lastexam.ai/',
        plainMeaning: '사실 단답 QA 평가입니다. 모델이 모르면 모른다고 하고, 알 때는 짧고 정확한 사실 답을 내는지 봅니다.',
        exampleDatasetUrl: '/assets/benchmark-examples/simpleqa-sample.json',
        exampleDatasetNote: '원본 SimpleQA 문항이 아니라 웹에서 데이터셋 구조를 이해하기 위한 합성 예시입니다.',
        exampleRows: [
            {
                id: 'simpleqa-demo-factual-001',
                task: '단답 사실 질의',
                input: '정답이 하나로 검증 가능한 짧은 질문을 제공하고 모델이 바로 답하게 합니다.',
                expected: '참조 정답을 완전히 포함하고 모순 정보가 없는 짧은 답',
                scoring: 'grader가 CORRECT, INCORRECT, NOT_ATTEMPTED 중 하나로 분류합니다.',
            },
            {
                id: 'simpleqa-demo-abstain-002',
                task: '모름 응답 구분',
                input: '모델이 확신하기 어려운 사실 질문을 주고 추측과 모름을 분리합니다.',
                expected: '정답을 모르면 모순 없이 not attempted로 남기는 응답',
                scoring: '오답과 미응답을 분리해 hallucination을 과대평가하지 않도록 봅니다.',
            },
            {
                id: 'simpleqa-demo-calibration-003',
                task: '확신도 보정',
                input: '모델 답과 self-reported confidence를 함께 수집합니다.',
                expected: '높은 confidence일수록 실제 정답률도 높아지는 응답 분포',
                scoring: '정답률, 오답률, 미응답률과 confidence calibration을 별도로 비교합니다.',
            },
        ],
        references: [
            {
                title: 'Introducing SimpleQA',
                source: 'OpenAI',
                url: 'https://openai.com/index/introducing-simpleqa/',
                note: 'OpenAI 소개 글은 SimpleQA가 짧고 검증 가능한 factual question으로 hallucination과 미응답을 분리해 측정한다고 설명한다.',
            },
            {
                title: 'Measuring short-form factuality in large language models',
                source: 'arXiv',
                url: 'https://arxiv.org/abs/2411.04368',
                note: 'SimpleQA 논문은 benchmark 설계, 데이터 품질 검증, grading 방식, calibration 분석을 다룬다.',
            },
            {
                title: 'openai/simple-evals simpleqa_eval.py',
                source: 'GitHub',
                url: 'https://github.com/openai/simple-evals/blob/main/simpleqa_eval.py',
                note: 'simple-evals 구현은 SimpleQA 데이터 로딩 URL, grader template, CORRECT/INCORRECT/NOT_ATTEMPTED 집계 방식을 보여준다.',
            },
            {
                title: 'SimpleQA test set CSV',
                source: 'OpenAI public blob',
                url: 'https://openaipublic.blob.core.windows.net/simple-evals/simple_qa_test_set.csv',
                note: '공개 CSV는 SimpleQA 평가 실행에 쓰이는 문제와 정답 필드를 제공한다.',
            },
        ],
        visuals: [
            {
                src: 'https://www.lastexam.ai/_next/static/media/classics.81111c73.png',
                alt: 'SimpleQA 사실성 평가 흐름 다이어그램',
                caption: 'SimpleQA는 짧은 사실 질문, 모델 답변, 참조 정답, grader 판정을 분리해 hallucination을 측정한다.',
                sourceTitle: 'Introducing SimpleQA',
                sourceUrl: 'https://openai.com/index/introducing-simpleqa/',
                referenceIndex: 1,
                featured: true,
            },
            {
                src: 'https://raw.githubusercontent.com/centerforaisafety/hle/main/images/hle_logo.png',
                alt: 'SimpleQA 채점 라벨 다이어그램',
                caption: '채점은 correct, incorrect, not attempted를 분리한다. 틀린 추측과 모른다고 답한 경우를 같은 실패로 합치지 않는 점이 핵심이다.',
                sourceTitle: 'Humanity\'s Last Exam logo image',
                sourceUrl: 'https://github.com/centerforaisafety/hle/tree/main/images',
                referenceIndex: 3,
            },
            {
                src: 'https://raw.githubusercontent.com/centerforaisafety/hle/main/images/hle_logo.png',
                alt: 'SimpleQA 확신도 보정 개념 그래프',
                caption: 'SimpleQA는 정답률뿐 아니라 confidence와 consistency가 실제 정확도와 맞는지 보는 calibration 분석에도 쓰인다.',
                sourceTitle: 'Humanity\'s Last Exam logo image',
                sourceUrl: 'https://github.com/centerforaisafety/hle/tree/main/images',
                referenceIndex: 2,
                featured: true,
            },
        ],
        demoIdeas: [
            '단답 평가 데모: 질문, 참조 정답, 모델 답변을 나란히 보여주고 grader가 correct, incorrect, not attempted로 분류하는 과정을 표시한다.',
            '오답 분석 데모: 정답을 일부 포함한 답, 모순이 섞인 답, 모른다고 한 답을 분리해 hallucination 유형을 보여준다.',
            '확신도 데모: 모델이 제시한 confidence 구간별 실제 정답률을 그려 과신 여부를 확인한다.',
            '내부 FAQ 전환 데모: 사내 제품/정책 FAQ를 단답 factual question으로 바꿔 공개 SimpleQA와 별도 지표로 비교한다.',
        ],
    },
    {
        slug: 'helm',
        name: 'HELM',
        category: '추론 / 지식 / 선호',
        categoryId: 'reasoning',
        badge: 'holistic eval',
        summary: 'HELM은 정확도만이 아니라 견고성, 공정성, 편향, 독성, 효율성을 같이 보는 평가 프레임워크다.',
        measures: '여러 시나리오에서 accuracy, calibration, robustness, fairness, toxicity, efficiency를 함께 측정한다.',
        evaluation: 'scenario와 metric을 분리해 모델을 다면적으로 비교하는 방식이다.',
        bestFor: '운영 모델 후보를 단일 성능 점수가 아니라 리스크 지표까지 포함해 비교할 때 좋다.',
        watchouts: '프레임워크가 넓은 만큼 특정 업무의 end-to-end 성공률은 내부 eval이 필요하다.',
        internalEval: '정확도, 비용, latency, toxicity, 개인정보 위험을 같은 대시보드에서 함께 추적한다.',
        sourceTitle: 'Humanity\'s Last Exam difficulty image',
        sourceUrl: 'https://www.lastexam.ai/',
        plainMeaning: '종합 성적표 평가입니다. 정확도 하나가 아니라 효율, 편향, 안전성 같은 여러 지표를 한 프레임에서 봅니다.',
        exampleDatasetUrl: '/assets/benchmark-examples/helm-sample.json',
        exampleDatasetNote: '원본 HELM run spec이 아니라 웹에서 scenario와 metric 구조를 이해하기 위한 합성 예시입니다.',
        exampleRows: [
            {
                id: 'helm-demo-scenario-001',
                task: 'Scenario 단위 평가',
                input: '질문응답, 요약, 정보검색처럼 서로 다른 scenario를 같은 실행 규칙으로 평가합니다.',
                expected: 'scenario별 model generation과 reference 또는 grader output',
                scoring: 'accuracy, calibration, robustness처럼 scenario에 맞는 metric을 분리해 집계합니다.',
            },
            {
                id: 'helm-demo-risk-002',
                task: '운영 리스크 지표',
                input: 'toxicity, bias, fairness, privacy 같은 위험 측정용 prompt와 subgroup 정보를 제공합니다.',
                expected: '정책 위반 또는 subgroup 성능 차이를 판단할 수 있는 응답 로그',
                scoring: '성능 점수와 별도로 fairness, toxicity, robustness metric을 병렬로 봅니다.',
            },
            {
                id: 'helm-demo-efficiency-003',
                task: '효율성 추적',
                input: '같은 모델과 prompt 조건에서 latency, token count, cost proxy를 함께 수집합니다.',
                expected: '정확도와 운영 비용을 같은 run metadata로 연결한 결과',
                scoring: '모델 순위가 아니라 업무 제약에 맞는 tradeoff를 비교합니다.',
            },
        ],
        references: [
            {
                title: 'Holistic Evaluation of Language Models',
                source: 'Stanford CRFM',
                url: 'https://crfm.stanford.edu/2022/11/17/helm.html',
                note: 'CRFM 소개 글은 HELM이 accuracy만이 아니라 robustness, fairness, toxicity, efficiency 등 여러 측면의 투명한 평가를 목표로 한다고 설명한다.',
            },
            {
                title: 'Holistic Evaluation of Language Models',
                source: 'arXiv',
                url: 'https://arxiv.org/abs/2211.09110',
                note: 'HELM 논문은 benchmark coverage, scenario, metric, model 결과 공개 방식을 체계화한다.',
            },
            {
                title: 'stanford-crfm/helm',
                source: 'GitHub',
                url: 'https://github.com/stanford-crfm/helm',
                note: '공식 저장소는 HELM 실행 프레임워크, scenario와 metric 구현, 결과 재현 절차를 제공한다.',
            },
            {
                title: 'HELM latest results',
                source: 'Stanford CRFM',
                url: 'https://crfm.stanford.edu/helm/latest/',
                note: '최신 결과 페이지는 HELM run 결과를 모델과 metric 축으로 탐색할 수 있게 제공한다.',
            },
        ],
        visuals: [
            {
                src: 'https://www.lastexam.ai/_next/static/media/difficulty_comparison_new.54376f68.png',
                alt: 'HELM 다면 지표 지도 다이어그램',
                caption: 'HELM은 정확도 하나로 모델을 줄 세우지 않고 scenario와 metric을 나눠 성능, 견고성, 공정성, 독성, 효율성을 함께 본다.',
                sourceTitle: 'Stanford CRFM HELM',
                sourceUrl: 'https://crfm.stanford.edu/2022/11/17/helm.html',
                referenceIndex: 1,
                featured: true,
            },
            {
                src: 'https://www.lastexam.ai/_next/static/media/difficulty_comparison_new.54376f68.png',
                alt: 'HELM 평가 파이프라인 다이어그램',
                caption: '평가 실행은 scenario 정의, model generation, metric computation, 결과 공개로 이어진다. 내부 평가도 이 흐름을 따라 재현성을 확보해야 한다.',
                sourceTitle: 'Humanity\'s Last Exam difficulty image',
                sourceUrl: 'https://www.lastexam.ai/',
                referenceIndex: 3,
            },
            {
                src: 'https://raw.githubusercontent.com/centerforaisafety/hle/main/images/hle_logo.png',
                alt: 'HELM 모델별 지표 매트릭스 개념도',
                caption: 'HELM식 읽기는 전체 평균보다 metric별 약점을 보는 방식이다. 한 모델이 모든 축에서 우수하다고 가정하지 않는다.',
                sourceTitle: 'Humanity\'s Last Exam logo image',
                sourceUrl: 'https://github.com/centerforaisafety/hle/tree/main/images',
                referenceIndex: 4,
                featured: true,
            },
        ],
        demoIdeas: [
            '다면 점수 데모: 같은 모델의 accuracy, robustness, fairness, toxicity, efficiency를 카드가 아닌 표와 matrix로 나눠 보여준다.',
            'scenario 선택 데모: 사내 업무를 QA, 요약, 분류, 안전성 scenario로 나누고 각 scenario에 필요한 metric만 선택한다.',
            '리스크 추적 데모: 모델 업그레이드 전후 accuracy가 올랐지만 toxicity나 subgroup gap이 악화되는 경우를 시각화한다.',
            '내부 HELM 전환 데모: golden set 실행 결과를 HELM처럼 scenario, metric, run metadata로 저장해 재현 가능한 비교 화면을 만든다.',
        ],
    },
    {
        slug: 'lmarena',
        name: 'LMArena',
        category: '추론 / 지식 / 선호',
        categoryId: 'reasoning',
        badge: 'human preference',
        summary: 'LMArena는 익명 pairwise 투표로 사용자가 더 선호한 모델 응답을 비교하는 대화 품질 리더보드다.',
        measures: '대화 자연스러움, 지시 이행, 답변 유용성, 스타일 선호를 사용자 투표로 본다.',
        evaluation: '두 모델 응답을 블라인드로 보여주고 승패를 누적해 Elo 계열 점수를 산출한다.',
        bestFor: '실사용자가 체감하는 범용 챗봇 품질과 선호를 빠르게 보는 데 적합하다.',
        watchouts: '스타일, 길이, 표본 구성, 사용자층에 영향을 받으므로 정답형 업무 성능과 혼동하면 안 된다.',
        internalEval: '내부 사용자 blind preference test를 병행하고 비용/정확성 지표와 함께 본다.',
        sourceTitle: 'Humanity\'s Last Exam difficulty image',
        sourceUrl: 'https://www.lastexam.ai/',
        plainMeaning: '사람 선호 대결 평가입니다. 두 모델 답변을 나란히 놓고 사람이 어느 쪽을 더 선호하는지 봅니다.',
        exampleDatasetUrl: '/assets/benchmark-examples/lmarena-sample.json',
        exampleDatasetNote: '원본 LMArena 투표 로그가 아니라 웹에서 pairwise preference 구조를 이해하기 위한 합성 예시입니다.',
        exampleRows: [
            {
                id: 'lmarena-demo-battle-001',
                task: '익명 pairwise battle',
                input: '같은 사용자 prompt에 대해 모델 A와 모델 B의 익명 응답을 나란히 보여줍니다.',
                expected: '사용자가 더 유용하다고 느낀 응답을 선택하거나 tie를 기록합니다.',
                scoring: '승패, tie, vote metadata를 누적해 Arena score 또는 Elo 계열 점수를 갱신합니다.',
            },
            {
                id: 'lmarena-demo-style-002',
                task: '스타일 선호 확인',
                input: '길이, 친절함, 포맷팅, 단호함처럼 정답과 분리되는 스타일 차이가 있는 응답쌍을 비교합니다.',
                expected: '사용자층별 선호와 실제 업무 정확성의 차이를 분리한 기록',
                scoring: 'pairwise win rate와 내부 정답/품질 지표를 함께 비교합니다.',
            },
            {
                id: 'lmarena-demo-segment-003',
                task: '업무 세그먼트별 선호',
                input: '코딩, 요약, 검색, 창작처럼 업무 유형별 prompt 묶음을 나누어 투표합니다.',
                expected: '전체 평균이 아니라 segment별 선호 모델',
                scoring: '전체 leaderboard 점수와 segment별 preference를 따로 해석합니다.',
            },
        ],
        references: [
            {
                title: 'Chatbot Arena',
                source: 'LMArena blog',
                url: 'https://oldblog.lmarena.ai/blog/2023/arena/',
                note: '초기 Chatbot Arena 글은 익명 randomized battle, crowdsourced vote, Elo 기반 leaderboard라는 핵심 방식을 설명한다.',
            },
            {
                title: 'Chatbot Arena: An Open Platform for Evaluating LLMs by Human Preference',
                source: 'arXiv',
                url: 'https://arxiv.org/abs/2403.04132',
                note: 'Chatbot Arena 논문은 pairwise human preference 수집, ranking 신뢰도, 통계적 비교 방법을 정리한다.',
            },
            {
                title: 'LMArena leaderboard',
                source: 'LMArena',
                url: 'https://lmarena.ai/leaderboard/',
                note: '현재 leaderboard는 arena별 점수, 투표 수, 신뢰구간을 함께 보여주며 점수를 절대 업무 성능으로 합치지 말아야 함을 드러낸다.',
            },
            {
                title: 'LMArena GitHub organization',
                source: 'GitHub',
                url: 'https://github.com/lmarena',
                note: 'LMArena GitHub 조직은 leaderboard 방법론과 arena 관련 공개 코드, 연구 산출물을 제공한다.',
            },
        ],
        visuals: [
            {
                src: 'https://www.lastexam.ai/_next/static/media/difficulty_comparison_new.54376f68.png',
                alt: 'LMArena pairwise 투표 흐름 다이어그램',
                caption: 'LMArena는 한 prompt에 대한 두 익명 응답을 사람이 비교하고, 선호 투표가 누적되며 모델 점수를 갱신하는 방식이다.',
                sourceTitle: 'Chatbot Arena launch blog',
                sourceUrl: 'https://oldblog.lmarena.ai/blog/2023/arena/',
                referenceIndex: 1,
                featured: true,
            },
            {
                src: 'https://raw.githubusercontent.com/centerforaisafety/hle/main/images/dataset.png',
                alt: 'LMArena 점수와 신뢰구간 개념도',
                caption: 'leaderboard 점수는 투표 수와 confidence interval을 함께 봐야 한다. 작은 점수 차이는 실제 선호 차이로 과대 해석하기 쉽다.',
                sourceTitle: 'Humanity\'s Last Exam dataset image',
                sourceUrl: 'https://github.com/centerforaisafety/hle/blob/main/images/dataset.png',
                referenceIndex: 3,
                featured: true,
            },
            {
                src: 'https://www.lastexam.ai/_next/static/media/classics.81111c73.png',
                alt: 'LMArena 선호 편향 점검 다이어그램',
                caption: '사용자 선호는 답변 길이, 말투, prompt 구성, 사용자층에 영향을 받는다. 내부 도입 판단에는 blind preference test와 정답형 평가를 함께 둔다.',
                sourceTitle: 'Humanity\'s Last Exam example image',
                sourceUrl: 'https://www.lastexam.ai/',
                referenceIndex: 2,
            },
        ],
        demoIdeas: [
            '익명 투표 데모: 같은 업무 prompt에 대한 두 모델 응답을 모델명 없이 보여주고 선택 후 모델명을 공개한다.',
            '선호와 정확성 분리 데모: 사용자가 선호한 답변이 실제 정답 또는 정책 기준을 만족했는지 별도 채점한다.',
            '세그먼트 leaderboard 데모: 전체 선호 점수 대신 코딩, 요약, 고객지원, 한국어 답변처럼 업무별 pairwise 결과를 분리한다.',
            '내부 Arena 전환 데모: 사내 사용자 blind vote, 비용, latency, 안전성 로그를 함께 저장해 공개 LMArena 점수와 별도 비교한다.',
        ],
    },
    {
        slug: 'arc-agi-2',
        name: 'ARC-AGI-2',
        category: '추론 / 지식 / 선호',
        categoryId: 'reasoning',
        badge: 'abstraction',
        summary: 'ARC-AGI-2는 작은 격자 패턴에서 규칙을 추론해 새 예시에 일반화하는 추상 reasoning 평가다.',
        measures: '암기나 언어 지식보다 새로운 규칙 발견, pattern abstraction, few-shot generalization을 본다.',
        evaluation: '입출력 예시를 보고 테스트 격자의 정답 출력을 생성하는 방식으로 채점한다.',
        bestFor: 'frontier model의 진짜 일반화 능력과 추상 문제 해결력을 비교할 때 유용하다.',
        watchouts: '업무 자동화 성공률이나 지식 정확도와는 거리가 있어 보조 지표로만 써야 한다.',
        internalEval: '정형 문서 변환이나 규칙 기반 데이터 정리 task를 unseen case로 만들어 본다.',
        sourceTitle: 'Humanity\'s Last Exam logo image',
        sourceUrl: 'https://github.com/centerforaisafety/hle/tree/main/images',
        plainMeaning: '추상 퍼즐 적응 평가입니다. 처음 보는 격자 규칙을 몇 개 예시만 보고 찾아내는지 봅니다.',
        exampleDatasetUrl: '/assets/benchmark-examples/arc-agi-2-sample.json',
        exampleDatasetNote: '원본 ARC-AGI-2 task가 아니라 웹에서 격자 추론 구조를 이해하기 위한 합성 예시입니다.',
        exampleRows: [
            {
                id: 'arc-agi-2-demo-rule-001',
                task: '격자 규칙 유도',
                input: '2-3개의 input/output 예시 격자를 보고 색상, 위치, 연결성, 개수 규칙을 추론합니다.',
                expected: '테스트 input 격자에 같은 변환 규칙을 적용한 output 격자',
                scoring: '모든 셀이 정답 격자와 일치해야 하며 pass@2처럼 제한된 시도 수를 함께 봅니다.',
            },
            {
                id: 'arc-agi-2-demo-composition-002',
                task: '복합 규칙 적용',
                input: '객체 분리, 이동, 색상 치환처럼 두 개 이상의 규칙이 동시에 필요한 예시를 제공합니다.',
                expected: '규칙 조합을 일관되게 적용한 테스트 격자',
                scoring: '부분적으로 맞은 셀보다 전체 task 성공 여부와 실패 유형을 우선 확인합니다.',
            },
            {
                id: 'arc-agi-2-demo-efficiency-003',
                task: '효율성 추적',
                input: '같은 task를 제한된 cost, 시간, 시도 횟수 안에서 풉니다.',
                expected: '정답 격자와 함께 사용한 탐색 비용, 시도 횟수, runtime metadata',
                scoring: '정답률만이 아니라 brute-force에 가까운 고비용 탐색인지 함께 기록합니다.',
            },
        ],
        references: [
            {
                title: 'ARC-AGI-2',
                source: 'ARC Prize',
                url: 'https://arcprize.org/arc-agi/2',
                note: '공식 ARC-AGI-2 페이지는 task 형식, 데이터셋 split, human calibration, efficiency measurement를 설명한다.',
            },
            {
                title: 'ARC-AGI-2: A New Challenge for Frontier AI Reasoning Systems',
                source: 'arXiv',
                url: 'https://arxiv.org/abs/2505.11831',
                note: '기술 보고서는 ARC-AGI-2의 설계 목표, human-facing calibration, 평가 set 구성, 모델 성능 해석을 다룬다.',
            },
            {
                title: 'arcprize/ARC-AGI-2',
                source: 'GitHub',
                url: 'https://github.com/arcprize/ARC-AGI-2',
                note: '공식 저장소는 공개 training task와 public evaluation task, 데이터 구조, changelog를 제공한다.',
            },
            {
                title: 'ARC Prize leaderboard',
                source: 'ARC Prize',
                url: 'https://arcprize.org/leaderboard',
                note: 'leaderboard는 ARC-AGI 계열 점수와 검증 상태를 확인하는 출처이며, 비용과 평가 set 조건을 함께 봐야 한다.',
            },
        ],
        visuals: [
            {
                src: 'https://raw.githubusercontent.com/centerforaisafety/hle/main/images/hle_logo.png',
                alt: 'ARC-AGI-2 격자 변환 task 형식 다이어그램',
                caption: 'ARC-AGI-2는 몇 개의 input/output 예시에서 숨은 변환 규칙을 추론하고, 보지 못한 test input에 같은 규칙을 적용한다.',
                sourceTitle: 'ARC-AGI-2 official page',
                sourceUrl: 'https://arcprize.org/arc-agi/2',
                referenceIndex: 1,
                featured: true,
            },
            {
                src: 'https://www.lastexam.ai/_next/static/media/difficulty_comparison_new.54376f68.png',
                alt: 'ARC-AGI-2 데이터셋 split 다이어그램',
                caption: '공개 training, public eval, semi-private eval, private eval을 분리해 overfitting과 leaderboard 누수를 줄인다.',
                sourceTitle: 'Humanity\'s Last Exam difficulty image',
                sourceUrl: 'https://www.lastexam.ai/',
                referenceIndex: 3,
                featured: true,
            },
            {
                src: 'https://raw.githubusercontent.com/centerforaisafety/hle/main/images/hle_logo.png',
                alt: 'ARC-AGI-2 효율성 측정 개념도',
                caption: 'ARC-AGI-2는 정답률뿐 아니라 cost와 시도 횟수를 함께 보며, brute-force search가 아니라 효율적인 추론을 요구한다.',
                sourceTitle: 'Humanity\'s Last Exam logo image',
                sourceUrl: 'https://github.com/centerforaisafety/hle/tree/main/images',
                referenceIndex: 2,
            },
        ],
        demoIdeas: [
            '격자 풀이 데모: 예시 input/output을 보여주고 모델이 추론한 규칙, 테스트 output, 셀 단위 diff를 함께 표시한다.',
            '복합 규칙 데모: symbolic interpretation, compositional reasoning, contextual rule application 유형별 실패 로그를 나눈다.',
            '효율성 데모: 같은 정답률이라도 사용한 시도 횟수, token, runtime, cost를 함께 보여 brute-force성 접근을 분리한다.',
            '내부 전환 데모: 사내 규칙 기반 데이터 변환 task를 ARC식 few-shot input/output 문제로 만들어 held-out generalization을 검증한다.',
        ],
    },
    {
        slug: 'frontiermath',
        name: 'FrontierMath',
        category: '추론 / 지식 / 선호',
        categoryId: 'reasoning',
        badge: 'advanced math',
        summary: 'FrontierMath는 frontier model도 해결하기 어려운 고난도 수학 문제로 구성된 평가다.',
        measures: '고급 수학 정리 적용, 긴 추론, 계산 실수 억제, 증명형 사고를 본다.',
        evaluation: '문제별 정답 일치와 풀이 검증을 사용하며, 공개 점수는 tool 사용 조건을 반드시 확인해야 한다.',
        bestFor: '수학, 알고리즘, 과학 계산 중심 업무에서 최상위 모델의 한계를 볼 때 맞다.',
        watchouts: '수학 고난도 성능은 일반 글쓰기, RAG, 웹 작업 성능과 독립적일 수 있다.',
        internalEval: '실제 분석 업무의 계산 문제를 정답 검증 가능한 형태로 만들고 풀이 로그를 저장한다.',
        sourceTitle: 'FrontierMath benchmark',
        sourceUrl: 'https://epoch.ai/frontiermath/the-benchmark',
        plainMeaning: '초고난도 수학 평가입니다. 전문 수학자가 만든 새 문제를 모델이 풀이와 계산으로 해결할 수 있는지 봅니다.',
        exampleDatasetUrl: '/assets/benchmark-examples/frontiermath-sample.json',
        exampleDatasetNote: '원본 FrontierMath 문항이 아니라 웹에서 고난도 수학 평가 구조를 이해하기 위한 합성 예시입니다.',
        exampleRows: [
            {
                id: 'frontiermath-demo-proof-001',
                task: '연구급 수학 문제',
                input: '수론, 해석학, 대수기하처럼 전문 분야의 새 문제와 명확한 최종 정답 형식을 제공합니다.',
                expected: '검증 가능한 최종 답과 충분한 풀이 근거',
                scoring: '정답 일치와 자동 검증 가능성을 우선하고, 풀이 로그는 실패 분석에 사용합니다.',
            },
            {
                id: 'frontiermath-demo-tier-002',
                task: '난도 tier 비교',
                input: 'Tiers 1-3 문제와 Tier 4 확장 문제를 도구 허용 조건별로 분리합니다.',
                expected: '같은 tier와 같은 공개/비공개 split 안에서만 비교 가능한 점수',
                scoring: 'tier, 공개 여부, 도구 사용, reasoning effort를 함께 기록합니다.',
            },
            {
                id: 'frontiermath-demo-internal-003',
                task: '내부 전문 문제 전환',
                input: '사내 연구, 알고리즘, 정산 검증 문제를 held-out 고난도 수학 task로 만듭니다.',
                expected: '정답 검증 가능한 수식, 코드, 증명 또는 계산 결과',
                scoring: '공개 FrontierMath 점수와 합치지 않고 내부 고난도 수학 회귀 지표로 따로 봅니다.',
            },
        ],
        references: [
            {
                title: 'FrontierMath benchmark',
                source: 'Epoch AI',
                url: 'https://epoch.ai/frontiermath/the-benchmark',
                note: 'Epoch AI 소개 글은 FrontierMath가 expert mathematician이 만든 새 고난도 문제로 구성되며 수학 연구자 수준의 풀이 시간을 요구한다고 설명한다.',
            },
            {
                title: 'FrontierMath: A Benchmark for Evaluating Advanced Mathematical Reasoning in AI',
                source: 'arXiv',
                url: 'https://arxiv.org/abs/2411.04872',
                note: '논문은 데이터셋 설계, 문제 분야, 오염 방지, 자동 검증, frontier model의 낮은 초기 성능을 정리한다.',
            },
            {
                title: 'About FrontierMath',
                source: 'Epoch AI',
                url: 'https://epoch.ai/frontiermath/tiers-1-4/about',
                note: 'About 페이지는 Tiers 1-4 구성, 공개/비공개 split, OpenAI 접근 조건과 평가 권한 같은 버전 정보를 제공한다.',
            },
            {
                title: 'Clarifying the creation and use of FrontierMath',
                source: 'Epoch AI',
                url: 'https://epoch.ai/blog/openai-and-frontiermath',
                note: 'Epoch AI는 FrontierMath 제작 의뢰와 일부 access 조건을 공개해 점수 해석 시 이해관계와 holdout 조건을 함께 봐야 함을 설명한다.',
            },
        ],
        visuals: [
            {
                src: 'https://epoch.ai/assets/images/posts/2024/the-benchmark/frontiermath-banner.png',
                alt: 'FrontierMath 공식 배너 이미지',
                caption: 'FrontierMath는 전문가가 새로 만든 고난도 수학 문제로 frontier model의 수학 추론 한계를 평가한다.',
                sourceTitle: 'FrontierMath benchmark',
                sourceUrl: 'https://epoch.ai/frontiermath/the-benchmark',
                referenceIndex: 1,
                featured: true,
            },
            {
                src: 'https://epoch.ai/assets/images/posts/2024/the-benchmark/frontiermath-vs-other-benchmarks.png',
                alt: 'FrontierMath와 다른 수학 벤치마크 난도 비교 이미지',
                caption: '기존 수학 벤치마크와 별도 축으로 보아야 할 정도로 FrontierMath의 난도가 높다는 점을 보여준다.',
                sourceTitle: 'FrontierMath benchmark',
                sourceUrl: 'https://epoch.ai/frontiermath/the-benchmark',
                referenceIndex: 1,
                featured: true,
            },
            {
                src: 'https://epoch.ai/assets/images/posts/2024/the-benchmark/current-models-vs-frontiermath.png',
                alt: '현대 모델의 FrontierMath 성능 비교 이미지',
                caption: '공식 글의 모델 성능 비교 이미지를 통해 공개 점수가 낮게 출발한 이유와 점수 해석 범위를 확인한다.',
                sourceTitle: 'FrontierMath benchmark',
                sourceUrl: 'https://epoch.ai/frontiermath/the-benchmark',
                referenceIndex: 1,
                featured: true,
            },
        ],
        demoIdeas: [
            '풀이 검증 데모: 최종 답, 핵심 보조정리, 계산 검증 코드, 실패 로그를 한 화면에서 분리해 보여준다.',
            'tier 비교 데모: Tiers 1-3과 Tier 4를 같은 차트에 합치지 않고 tier별 정답률과 도구 조건을 따로 표시한다.',
            '오염 점검 데모: 공개 샘플, private holdout, 내부 held-out 문제를 구분하고 모델이 어떤 문제에 접근 가능했는지 기록한다.',
            '내부 전환 데모: 사내 연구형 계산 문제를 정답 검증 가능한 FrontierMath식 task로 만들고 공개 점수와 별도 회귀 지표로 운영한다.',
        ],
    },
    {
        slug: 'aime',
        name: 'AIME',
        category: '추론 / 지식 / 선호',
        categoryId: 'reasoning',
        badge: 'contest math',
        summary: 'AIME는 미국 수학 경시 문제를 활용해 모델의 계산형 수학 추론과 정답 안정성을 비교하는 축이다.',
        measures: '정수 답을 요구하는 경시 수학 문제에서 다단계 풀이, 계산 정확도, 검산 능력을 본다.',
        evaluation: '문제별 정답 일치율을 사용하며, 연도와 prompt 조건에 따라 리더보드 값이 달라진다.',
        bestFor: '수학 추론 모델 비교와 reasoning mode의 효과를 확인하는 간단한 고난도 지표로 쓸 수 있다.',
        watchouts: '공개 기출 기반이라 오염 가능성이 있고, 업무형 수학 문제와는 분포가 다르다.',
        internalEval: '연도별 공개 기출 대신 사내에서 직접 만든 정답형 계산 문제를 held-out으로 둔다.',
        sourceTitle: 'Humanity\'s Last Exam difficulty image',
        sourceUrl: 'https://www.lastexam.ai/',
        plainMeaning: '경시 수학 평가입니다. 000-999 정수 답을 요구하는 문제에서 모델의 계산형 추론을 봅니다.',
        exampleDatasetUrl: '/assets/benchmark-examples/aime-sample.json',
        exampleDatasetNote: '원본 AIME 문항이 아니라 웹에서 정수 답 수학 추론 구조를 이해하기 위한 합성 예시입니다.',
        exampleRows: [
            {
                id: 'aime-demo-integer-001',
                task: '정수 답 경시 수학',
                input: '대수, 조합, 기하, 수론 문제를 제시하고 000-999 범위의 정수 답을 요구합니다.',
                expected: '세 자리로 표기 가능한 단일 정수 답',
                scoring: '풀이 과정과 별도로 최종 정수 답을 추출해 exact match로 채점합니다.',
            },
            {
                id: 'aime-demo-year-split-002',
                task: '연도별 기출 분리',
                input: 'AIME I/II와 연도 정보를 함께 기록하고 공개 기출 접근 가능성을 구분합니다.',
                expected: '같은 연도, 같은 세트, 같은 prompt 조건에서 비교 가능한 정답률',
                scoring: '공개된 과거 문제와 최신 또는 내부 held-out 문제를 분리합니다.',
            },
            {
                id: 'aime-demo-format-003',
                task: '답 형식 오류 점검',
                input: '모델 풀이가 길어도 마지막 답을 세 자리 정수로 명시하도록 요구합니다.',
                expected: '예: 007, 042, 314처럼 zero-padding 가능한 최종 답',
                scoring: '정답 추론 실패와 answer extraction 실패를 따로 기록합니다.',
            },
        ],
        references: [
            {
                title: 'MAA Invitational Competitions',
                source: 'Mathematical Association of America',
                url: 'https://maa.org/maa-invitational-competitions/',
                note: 'MAA는 AIME가 AMC 10/12 성적 우수자를 대상으로 하는 초청형 고난도 수학 시험이라고 설명한다.',
            },
            {
                title: 'AIME Problems and Solutions',
                source: 'Art of Problem Solving',
                url: 'https://artofproblemsolving.com/wiki/index.php/AIME_Problems_and_Solutions',
                note: 'AoPS archive는 연도별 AIME I/II 문제와 해설을 정리해 공개 기출 기반 평가의 접근 가능성과 오염 리스크를 보여준다.',
            },
            {
                title: 'MAA AMC policies',
                source: 'Mathematical Association of America',
                url: 'https://maa.org/student-programs/amc/maa-american-mathematics-competitions-policies/',
                note: 'MAA 정책 문서는 AIME qualification, 응시 규칙, AMC 계열과 USAMO/USAJMO 연결 흐름을 설명한다.',
            },
            {
                title: 'MathArena: Evaluating LLMs on Uncontaminated Math Competitions',
                source: 'arXiv',
                url: 'https://arxiv.org/abs/2505.23281',
                note: 'MathArena 논문은 AIME 같은 새 competition 문제를 빠르게 평가에 쓰면 공개 기출 오염 문제를 줄일 수 있음을 논의한다.',
            },
        ],
        visuals: [
            {
                src: 'https://www.lastexam.ai/_next/static/media/difficulty_comparison_new.54376f68.png',
                alt: 'AIME 시험 형식 다이어그램',
                caption: 'AIME는 15개 단답형 문제를 3시간 안에 풀고, 각 답은 000부터 999까지의 정수로 제출하는 형식이다.',
                sourceTitle: 'MAA Invitational Competitions',
                sourceUrl: 'https://maa.org/maa-invitational-competitions/',
                referenceIndex: 1,
                featured: true,
            },
            {
                src: 'https://raw.githubusercontent.com/centerforaisafety/hle/main/images/dataset.png',
                alt: 'AIME 답 추출과 채점 흐름 다이어그램',
                caption: '모델 평가는 긴 풀이에서 최종 정수를 추출하고 zero-padding 후 exact match로 비교한다. 풀이 오류와 답 형식 오류를 분리해야 한다.',
                sourceTitle: 'Humanity\'s Last Exam dataset image',
                sourceUrl: 'https://github.com/centerforaisafety/hle/blob/main/images/dataset.png',
                referenceIndex: 2,
                featured: true,
            },
            {
                src: 'https://raw.githubusercontent.com/centerforaisafety/hle/main/images/hle_logo.png',
                alt: 'AIME 공개 기출과 held-out 분리 다이어그램',
                caption: 'AIME는 오래된 공개 기출이 많아 오염 가능성이 높다. 최신 competition, 내부 held-out, 공개 archive 결과를 구분해 해석한다.',
                sourceTitle: 'Humanity\'s Last Exam logo image',
                sourceUrl: 'https://github.com/centerforaisafety/hle/tree/main/images',
                referenceIndex: 4,
            },
        ],
        demoIdeas: [
            '정수 답 추출 데모: 모델 풀이에서 마지막 답을 찾아 000-999 형식으로 정규화하고 정답표와 exact match를 비교한다.',
            '연도별 비교 데모: AIME 2024, AIME 2025, 내부 held-out 문제를 한 차트에 합치지 않고 세트별로 분리해 보여준다.',
            '오답 유형 데모: 개념 오류, 계산 실수, 답 추출 실패, formatting 실패를 로그로 나누어 표시한다.',
            '내부 전환 데모: 사내 계산형 문제를 AIME처럼 단일 정수 답이 있는 held-out 문제로 만들고 공개 기출 점수와 별도 관리한다.',
        ],
    },
    {
        slug: 'math-500',
        name: 'MATH-500',
        category: '추론 / 지식 / 선호',
        categoryId: 'reasoning',
        badge: 'math reasoning',
        summary: 'MATH-500은 MATH 계열 문제 subset으로 수학 풀이 능력을 빠르게 비교하는 데 널리 쓰인다.',
        measures: '대수, 기하, 조합, 수론 등 다양한 수학 문제에서 풀이 계획과 최종 답 정확도를 본다.',
        evaluation: '최종 답을 기준으로 채점하며, 일부 환경은 풀이 과정의 self-consistency를 함께 사용한다.',
        bestFor: '수학 reasoning 회귀 테스트나 모델 업그레이드 전후 비교에 적합하다.',
        watchouts: '상위 모델은 일부 포화되어 더 어려운 FrontierMath나 최신 경시 문제와 함께 봐야 한다.',
        internalEval: '사내 계산/정산/분석 문제를 MATH-500처럼 정답 검증 가능한 단위로 만든다.',
        sourceTitle: 'Humanity\'s Last Exam dataset image',
        sourceUrl: 'https://github.com/centerforaisafety/hle/blob/main/images/dataset.png',
        plainMeaning: '수학 풀이 평가입니다. 수학 문제를 풀고 최종 답을 정규화해 맞히는지 보는 작은 held-out 세트입니다.',
        exampleDatasetUrl: '/assets/benchmark-examples/math-500-sample.json',
        exampleDatasetNote: '원본 MATH-500 문항이 아니라 웹에서 split, subject, answer grading 구조를 이해하기 위한 합성 예시입니다.',
        exampleRows: [
            {
                id: 'math500-demo-answer-001',
                task: '최종 답 수학 추론',
                input: 'competition math 문제와 subject, level, 출처 split을 함께 저장하고 풀이를 생성합니다.',
                expected: '정규화 가능한 최종 답과 필요하면 LaTeX boxed answer',
                scoring: '모델 출력에서 최종 답을 추출한 뒤 MATH/PRM800K 계열 grader로 ground truth와 비교합니다.',
            },
            {
                id: 'math500-demo-subject-002',
                task: '과목별 약점 진단',
                input: 'algebra, geometry, number theory, counting and probability 등 subject label을 보존합니다.',
                expected: '전체 accuracy와 subject별 accuracy를 분리한 결과',
                scoring: '500개 평균만 보지 않고 subject와 difficulty별 실패 유형을 함께 집계합니다.',
            },
            {
                id: 'math500-demo-split-003',
                task: 'held-out split 추적',
                input: 'OpenAI PRM800K가 지정한 500개 held-out 문제와 다른 MATH test 문제를 구분합니다.',
                expected: '같은 500문제, 같은 prompt, 같은 answer grader 조건에서 비교 가능한 점수',
                scoring: 'MATH full test, MATH-500, 자체 held-out 결과를 서로 다른 series로 관리합니다.',
            },
        ],
        references: [
            {
                title: 'PRM800K: A Process Supervision Dataset',
                source: 'OpenAI GitHub',
                url: 'https://github.com/openai/prm800k',
                note: 'OpenAI PRM800K 저장소는 MATH test split 중 4,500개를 학습 확장에 쓰고 나머지 500개 held-out 문제로 평가했다는 MATH split 설명과 grading 코드를 제공한다.',
            },
            {
                title: 'Let\'s Verify Step by Step',
                source: 'OpenAI',
                url: 'https://cdn.openai.com/improving-mathematical-reasoning-with-process-supervision/Lets_Verify_Step_by_Step.pdf',
                note: '논문 부록은 500개 held-out 문제가 균등 무작위로 선택되었고 MATH test set의 subject와 difficulty 분포를 대표하도록 확인했다고 설명한다.',
            },
            {
                title: 'Measuring Mathematical Problem Solving With the MATH Dataset',
                source: 'arXiv',
                url: 'https://arxiv.org/abs/2103.03874',
                note: 'MATH 논문은 12,500개 competition math 문제와 단계별 해설을 제공하는 원본 데이터셋의 목적과 난도를 설명한다.',
            },
            {
                title: 'hendrycks/math',
                source: 'GitHub',
                url: 'https://github.com/hendrycks/math',
                note: '원본 MATH 저장소는 NeurIPS 2021 MATH dataset의 loader와 evaluation code, 데이터셋 다운로드 경로를 제공한다.',
            },
            {
                title: 'HuggingFaceH4/MATH-500',
                source: 'Hugging Face',
                url: 'https://huggingface.co/datasets/HuggingFaceH4/MATH-500',
                note: 'Hugging Face dataset card는 MATH-500을 OpenAI Let\'s Verify Step by Step 논문에서 만든 500개 MATH subset으로 정리하고 rows 수를 명시한다.',
            },
        ],
        visuals: [
            {
                src: 'https://raw.githubusercontent.com/centerforaisafety/hle/main/images/dataset.png',
                alt: 'MATH-500 held-out split 다이어그램',
                caption: 'MATH-500은 PRM800K 작업에서 MATH test split 중 4,500개를 학습 확장에 사용한 뒤 남긴 500개 held-out 문제다. full MATH test와 같은 이름으로 섞어 비교하면 안 된다.',
                sourceTitle: 'OpenAI PRM800K',
                sourceUrl: 'https://github.com/openai/prm800k',
                referenceIndex: 1,
                featured: true,
            },
            {
                src: 'https://www.lastexam.ai/_next/static/media/classics.81111c73.png',
                alt: 'MATH-500 subject와 difficulty 구성 다이어그램',
                caption: 'MATH-500 점수는 전체 평균만으로 읽기보다 algebra, geometry, number theory 같은 subject와 difficulty별 실패를 함께 봐야 한다.',
                sourceTitle: 'Humanity\'s Last Exam example image',
                sourceUrl: 'https://www.lastexam.ai/',
                referenceIndex: 2,
                featured: true,
            },
            {
                src: 'https://www.lastexam.ai/_next/static/media/difficulty_comparison_new.54376f68.png',
                alt: 'MATH-500 답 추출과 채점 흐름 다이어그램',
                caption: 'MATH-500 평가는 풀이 품질과 별도로 최종 답 추출, 수식 정규화, symbolic equality 확인이 점수에 영향을 준다. grader 버전과 prompt를 함께 고정해야 한다.',
                sourceTitle: 'Humanity\'s Last Exam difficulty image',
                sourceUrl: 'https://www.lastexam.ai/',
                referenceIndex: 1,
            },
        ],
        demoIdeas: [
            '답안 정규화 데모: 모델의 긴 풀이에서 boxed answer를 찾고 분수, 루트, LaTeX 표현을 정규화한 뒤 grader 결과를 보여준다.',
            'split 비교 데모: MATH full test, MATH-500, 내부 held-out을 같은 그래프에 합치지 않고 split별 series로 분리한다.',
            '과목별 실패 데모: algebra, geometry, number theory, counting and probability별 오답률과 answer extraction 실패를 함께 표시한다.',
            '내부 전환 데모: 사내 계산/정산 문제를 정답 검증 가능한 MATH-500식 JSON schema로 바꾸고 공개 점수와 별도 회귀 지표로 운영한다.',
        ],
    },
    {
        slug: 'bbh',
        name: 'BBH',
        category: '추론 / 지식 / 선호',
        categoryId: 'reasoning',
        badge: 'hard reasoning',
        summary: 'BIG-Bench Hard는 BIG-Bench 중 기존 모델이 어려워하던 reasoning task subset이다.',
        measures: '논리, symbolic reasoning, multi-step instruction following, 날짜/객체 추론 등을 본다.',
        evaluation: 'task별 exact match 또는 지정 metric으로 채점하고 평균 성능을 비교한다.',
        bestFor: '기본 reasoning 회귀 테스트와 chain-of-thought 효과 확인에 여전히 참고할 수 있다.',
        watchouts: '오래 공개된 세트라 최신 frontier model 비교에는 오염과 포화 리스크가 크다.',
        internalEval: 'BBH task 유형을 참고하되 실제 업무 규칙과 데이터로 새 문제를 만든다.',
        sourceTitle: 'Humanity\'s Last Exam example image',
        sourceUrl: 'https://www.lastexam.ai/',
        plainMeaning: '어려운 추론 과제 평가입니다. 논리, 규칙, 언어 추론처럼 단순 지식암기로 풀기 어려운 문제를 봅니다.',
        exampleDatasetUrl: '/assets/benchmark-examples/bbh-sample.json',
        exampleDatasetNote: '원본 BBH 문항이 아니라 웹에서 task family, prompt condition, exact-match 구조를 이해하기 위한 합성 예시입니다.',
        exampleRows: [
            {
                id: 'bbh-demo-symbolic-001',
                task: 'symbolic reasoning',
                input: 'Boolean expression, Dyck language, object tracking처럼 규칙 기반 입력을 제시합니다.',
                expected: '정해진 target 문자열 또는 선택지',
                scoring: '출력에서 최종 답을 정규화한 뒤 task별 target과 exact match로 비교합니다.',
            },
            {
                id: 'bbh-demo-language-002',
                task: 'language and commonsense reasoning',
                input: 'date understanding, causal judgement, disambiguation QA처럼 짧은 자연어 상황을 제공합니다.',
                expected: '문항이 요구하는 단일 답 또는 선택지',
                scoring: '같은 prompt template, 같은 few-shot/CoT 조건에서 task별 accuracy를 계산합니다.',
            },
            {
                id: 'bbh-demo-cot-003',
                task: 'prompt condition audit',
                input: 'direct few-shot prompt와 chain-of-thought prompt를 같은 task에 각각 적용합니다.',
                expected: 'prompt 조건별로 분리된 정답률과 실패 유형',
                scoring: 'CoT 사용 여부가 점수를 크게 바꾸므로 direct와 CoT 결과를 같은 series로 합치지 않습니다.',
            },
        ],
        references: [
            {
                title: 'BIG-Bench Hard',
                source: 'GitHub',
                url: 'https://github.com/suzgunmirac/BIG-Bench-Hard',
                note: '공식 저장소는 BBH가 BIG-Bench에서 고른 23개 challenging task이며 data, CoT prompt, Codex 출력 예시를 함께 제공한다고 설명한다.',
            },
            {
                title: 'Challenging BIG-Bench Tasks and Whether Chain-of-Thought Can Solve Them',
                source: 'arXiv',
                url: 'https://arxiv.org/abs/2210.09261',
                note: 'BBH 논문은 기존 BIG-Bench 평가에서 평균 human-rater를 넘지 못한 23개 task를 선정하고 CoT prompting 효과를 분석한다.',
            },
            {
                title: 'BBH task files',
                source: 'GitHub',
                url: 'https://github.com/suzgunmirac/BIG-Bench-Hard/tree/main/bbh',
                note: 'task 디렉터리는 boolean expressions, date understanding, logical deduction, word sorting 등 23개 JSON task와 각 task 설명을 제공한다.',
            },
            {
                title: 'BIG-bench',
                source: 'Google GitHub',
                url: 'https://github.com/google/BIG-bench',
                note: '원본 BIG-Bench 저장소는 200개가 넘는 task로 구성된 협업형 benchmark이며 BBH는 이 중 어려웠던 task를 골라 만든 하위 세트다.',
            },
            {
                title: 'Beyond the Imitation Game',
                source: 'arXiv',
                url: 'https://arxiv.org/abs/2206.04615',
                note: 'BIG-Bench 논문은 204개 task와 다양한 분야의 평가 범위를 설명해 BBH가 어떤 모체 benchmark에서 나온 것인지 확인하게 한다.',
            },
            {
                title: 'Joschka/big_bench_hard',
                source: 'Hugging Face',
                url: 'https://huggingface.co/datasets/Joschka/big_bench_hard',
                note: 'Hugging Face dataset card는 BBH의 23개 task 구성과 row 수를 웹에서 확인할 수 있는 데이터셋 형태로 제공한다.',
            },
        ],
        visuals: [
            {
                src: 'https://www.lastexam.ai/_next/static/media/classics.81111c73.png',
                alt: 'BIG-Bench에서 BBH 23개 task를 고르는 흐름 다이어그램',
                caption: 'BBH는 BIG-Bench 전체 task 중 기존 모델이 평균 human-rater를 넘지 못한 23개 hard task를 따로 묶은 세트다.',
                sourceTitle: 'BIG-Bench Hard paper',
                sourceUrl: 'https://arxiv.org/abs/2210.09261',
                referenceIndex: 2,
                featured: true,
            },
            {
                src: 'https://www.lastexam.ai/_next/static/media/difficulty_comparison_new.54376f68.png',
                alt: 'BBH direct prompt와 chain-of-thought 조건 비교 다이어그램',
                caption: 'BBH 점수는 direct few-shot인지 CoT prompt인지에 따라 크게 달라진다. 모델 비교에서는 prompt 조건을 같은 series로 고정해야 한다.',
                sourceTitle: 'Humanity\'s Last Exam difficulty image',
                sourceUrl: 'https://www.lastexam.ai/',
                referenceIndex: 1,
                featured: true,
            },
            {
                src: 'https://www.lastexam.ai/_next/static/media/difficulty_comparison_new.54376f68.png',
                alt: 'BBH task family 분류 다이어그램',
                caption: 'BBH에는 symbolic, algorithmic, commonsense, language reasoning task가 섞여 있다. 평균 점수만 보지 않고 task family별 약점을 분리해야 한다.',
                sourceTitle: 'Humanity\'s Last Exam difficulty image',
                sourceUrl: 'https://www.lastexam.ai/',
                referenceIndex: 3,
            },
        ],
        demoIdeas: [
            'prompt 조건 비교 데모: 같은 BBH task를 direct few-shot과 CoT prompt로 실행하고 점수 차이를 별도 series로 표시한다.',
            'task family 진단 데모: symbolic, algorithmic, commonsense, language task를 묶어 평균 accuracy와 실패 예시를 함께 보여준다.',
            '오염 점검 데모: 공개 BBH 원문과 내부 변형 task를 구분하고 train corpus 노출 가능성이 있는 task는 별도 경고를 붙인다.',
            '내부 전환 데모: 사내 규칙 추론, 로그 순서 추적, 표 기반 QA를 BBH식 input-target JSON으로 만들어 공개 BBH와 별도 회귀 지표로 운영한다.',
        ],
    },
    {
        slug: 'swe-bench-pro',
        name: 'SWE-bench Pro',
        category: '코딩 / 소프트웨어 엔지니어링',
        categoryId: 'coding',
        badge: 'repo issue solving',
        summary: 'SWE-bench Pro는 실제 저장소 이슈를 해결하고 테스트를 통과시키는 장기 소프트웨어 작업 평가다.',
        measures: '코드 탐색, 버그 이해, 패치 작성, 테스트 실행, 회귀 방지까지 end-to-end 능력을 본다.',
        evaluation: '모델이 만든 patch를 repository test harness에 적용해 통과 여부를 task success로 계산한다.',
        bestFor: '코딩 agent나 IDE agent 모델을 고를 때 실제 이슈 해결 능력을 비교하는 핵심 지표다.',
        watchouts: '스캐폴드, tool budget, retry 정책 차이가 크므로 모델 단독 성능으로 읽으면 안 된다.',
        internalEval: '사내 repo의 과거 이슈를 재현 가능한 테스트와 함께 golden task로 만든다.',
        sourceTitle: 'SWE-Bench Pro public leaderboard',
        sourceUrl: 'https://labs.scale.com/leaderboard/swe_bench_pro_public',
        plainMeaning: '에이전트 코딩 평가입니다. 실제 저장소 이슈를 읽고 코드를 고친 뒤 테스트를 통과시키는지 봅니다.',
        exampleDatasetUrl: '/assets/benchmark-examples/swe-bench-pro-sample.json',
        exampleDatasetNote: '원본 SWE-Bench Pro 문항이 아니라 웹에서 repo issue, patch, Docker 평가 구조를 이해하기 위한 합성 예시입니다.',
        exampleRows: [
            {
                id: 'swe-pro-demo-instance-001',
                task: 'repository issue patching',
                input: 'repo, instance_id, base_commit, problem_statement, requirements, interface, dockerhub_tag를 함께 제공합니다.',
                expected: '문제를 해결하는 git diff 형식의 patch와 재현 가능한 테스트 결과',
                scoring: 'fail-to-pass 테스트가 통과하고 pass-to-pass 테스트가 계속 통과하면 resolved로 계산합니다.',
            },
            {
                id: 'swe-pro-demo-split-002',
                task: 'public, held-out, commercial split tracking',
                input: 'public OSS, held-out OSS, proprietary commercial subset을 분리해 기록합니다.',
                expected: '같은 subset, 같은 harness, 같은 tool budget에서 비교 가능한 resolve rate',
                scoring: 'subset과 repo language가 다르면 점수를 하나의 절대 순위로 합치지 않습니다.',
            },
            {
                id: 'swe-pro-demo-scaffold-003',
                task: 'agent scaffold audit',
                input: 'SWE-Agent, mini-swe-agent, 자체 agent처럼 검색/수정/테스트 루프가 다른 실행 환경을 기록합니다.',
                expected: 'model, scaffold, context retrieval, turn limit, Docker image 버전이 명시된 실행 로그',
                scoring: 'patch 성공률과 함께 search 실패, build 실패, flaky test, context miss를 분리합니다.',
            },
        ],
        references: [
            {
                title: 'SWE-Bench Pro public leaderboard',
                source: 'Scale Labs',
                url: 'https://labs.scale.com/leaderboard/swe_bench_pro_public',
                note: 'Scale Labs는 SWE-Bench Pro가 contamination, task diversity, underspecified issues, reproducible testing 문제를 다루며 four-stage workflow와 resolve rate 정의를 제공한다고 설명한다.',
            },
            {
                title: 'SWE-Bench Pro: Can AI Agents Solve Long-Horizon Software Engineering Tasks?',
                source: 'arXiv',
                url: 'https://arxiv.org/abs/2509.16941',
                note: '논문은 SWE-Bench Pro가 41개 repository의 1,865개 문제로 구성되고 public, held-out, commercial subset으로 나뉜다고 설명한다.',
            },
            {
                title: 'scaleapi/SWE-bench_Pro-os',
                source: 'GitHub',
                url: 'https://github.com/scaleapi/SWE-bench_Pro-os',
                note: '공식 저장소는 dataset 접근, Docker 기반 재현 평가, patch gather 형식, swe_bench_pro_eval.py 실행 흐름을 제공한다.',
            },
            {
                title: 'ScaleAI/SWE-bench_Pro',
                source: 'Hugging Face',
                url: 'https://huggingface.co/datasets/ScaleAI/SWE-bench_Pro',
                note: 'Hugging Face dataset card는 공개 test split 731 rows와 repo, instance_id, patch, test_patch, problem_statement, requirements, fail_to_pass, pass_to_pass 같은 컬럼을 보여준다.',
            },
            {
                title: 'SWE-bench',
                source: 'GitHub',
                url: 'https://github.com/SWE-bench/SWE-bench',
                note: '원본 SWE-bench는 GitHub issue와 codebase를 주고 patch를 생성해 Docker harness로 평가하는 기본 형식을 제공하며 Pro는 이 계열을 더 어렵게 확장한다.',
            },
        ],
        visuals: [
            {
                src: 'https://www.tbench.ai/og/ascii-logo-dark-1200x630.png',
                alt: 'SWE-Bench Pro task 생성과 검증 흐름 다이어그램',
                caption: 'SWE-Bench Pro task는 repository sourcing, Docker 환경 구성, commit harvesting, human augmentation과 test verification을 거쳐 만들어진다.',
                sourceTitle: 'SWE-Bench Pro methodology',
                sourceUrl: 'https://labs.scale.com/leaderboard/swe_bench_pro_public',
                referenceIndex: 1,
                featured: true,
            },
            {
                src: 'https://www.tbench.ai/og/ascii-logo-dark-1200x630.png',
                alt: 'SWE-Bench Pro patch 평가 게이트 다이어그램',
                caption: 'resolved 판정은 모델 patch가 fail-to-pass 테스트를 고치면서 pass-to-pass 테스트를 깨지 않는지 Docker 평가 환경에서 확인한다.',
                sourceTitle: 'Terminal-Bench raster image',
                sourceUrl: 'https://www.tbench.ai/',
                referenceIndex: 3,
                featured: true,
            },
            {
                src: 'https://filecdn.minimax.chat/public/58eca777-e31f-448a-9823-e2220e49b426.png',
                alt: 'SWE-Bench Pro public held-out commercial split 다이어그램',
                caption: '논문 기준 SWE-Bench Pro는 public, held-out, commercial subset으로 나뉜다. 공개 HF test split과 비공개 결과를 같은 표에서 비교할 때 subset을 반드시 표시한다.',
                sourceTitle: 'SWE-bench leaderboard raster image',
                sourceUrl: 'https://www.swebench.com/',
                referenceIndex: 2,
            },
        ],
        demoIdeas: [
            '평가 게이트 데모: 모델 patch를 적용한 뒤 fail-to-pass와 pass-to-pass 테스트 결과를 나란히 표시해 resolved 판단 근거를 보여준다.',
            'scaffold 비교 데모: 같은 모델을 SWE-Agent, mini-swe-agent, 내부 agent에서 실행하고 검색 실패, test 실행 실패, patch 실패를 분리한다.',
            'subset 분리 데모: public OSS, held-out OSS, commercial subset 결과를 한 평균으로 합치지 않고 repo language와 subset별 resolve rate로 표시한다.',
            '내부 전환 데모: 사내 이슈를 base commit, 문제 설명, 재현 테스트, 회귀 테스트, Docker image가 있는 SWE-Bench Pro식 task로 만든다.',
        ],
    },
    {
        slug: 'swe-bench-verified',
        name: 'SWE-bench Verified',
        category: '코딩 / 소프트웨어 엔지니어링',
        categoryId: 'coding',
        badge: 'legacy verified set',
        summary: 'SWE-bench Verified는 SWE-bench 중 사람이 검수한 500개 subset으로 한때 표준 코딩 지표로 쓰였다.',
        measures: '실제 GitHub issue 해결과 테스트 통과율을 작은 검수 세트에서 본다.',
        evaluation: 'patch 적용 후 테스트 통과 여부를 계산하며, 환경 재현성이 중요하다.',
        bestFor: '과거 논문과 모델 발표 자료를 해석하거나 baseline을 확인하는 데 유용하다.',
        watchouts: '포화와 데이터 노출 리스크가 커 frontier coding capability의 주 지표로는 약해졌다.',
        internalEval: 'Verified 점수보다 자체 저장소 이슈와 최신 held-out task를 우선한다.',
        sourceTitle: 'SWE-bench leaderboard raster image',
        sourceUrl: 'https://www.swebench.com/',
        plainMeaning: '레거시 코딩 이슈 평가입니다. 사람이 검수한 500개 GitHub 이슈를 풀어 과거 코딩 모델 baseline과 비교합니다.',
        exampleDatasetUrl: '/assets/benchmark-examples/swe-bench-verified-sample.json',
        exampleDatasetNote: '원본 SWE-bench Verified 문항이 아니라 웹에서 human filtering, issue patching, retirement risk 구조를 이해하기 위한 합성 예시입니다.',
        exampleRows: [
            {
                id: 'swe-verified-demo-instance-001',
                task: 'verified repository issue',
                input: 'repo, instance_id, base_commit, problem_statement, FAIL_TO_PASS, PASS_TO_PASS, difficulty를 함께 제공합니다.',
                expected: '문제 설명만 보고 작성한 patch가 숨겨진 테스트를 통과하는 결과',
                scoring: 'FAIL_TO_PASS와 PASS_TO_PASS가 모두 통과하면 resolved로 계산합니다.',
            },
            {
                id: 'swe-verified-demo-filter-002',
                task: 'human validation filter',
                input: 'issue description이 충분히 명확한지, FAIL_TO_PASS 테스트가 과도하게 좁거나 넓지 않은지 사람이 검수합니다.',
                expected: '원본 SWE-bench test sample 중 문제가 덜한 500개 subset',
                scoring: '검수 기준과 difficulty label을 함께 보존해 legacy baseline을 해석합니다.',
            },
            {
                id: 'swe-verified-demo-risk-003',
                task: 'legacy benchmark risk audit',
                input: '공개 repo, 공개 gold patch, 반복된 leaderboard 사용 여부와 모델 노출 가능성을 기록합니다.',
                expected: 'Verified 점수, Pro 점수, 내부 held-out 결과를 분리한 비교',
                scoring: '오염이나 flawed test 가능성이 있는 실패는 모델 한계와 benchmark 한계를 따로 기록합니다.',
            },
        ],
        references: [
            {
                title: 'Introducing SWE-bench Verified',
                source: 'OpenAI',
                url: 'https://openai.com/index/introducing-swe-bench-verified/',
                note: 'OpenAI는 1,699개 SWE-bench test sample을 전문 개발자가 검수해 문제가 덜한 500개 SWE-bench Verified subset을 만들었다고 설명한다.',
            },
            {
                title: 'Why SWE-bench Verified no longer measures frontier coding capabilities',
                source: 'OpenAI',
                url: 'https://openai.com/index/why-we-no-longer-evaluate-swe-bench-verified/',
                note: 'OpenAI는 2026년 분석에서 SWE-bench Verified가 test flaw와 contamination 때문에 frontier coding capability 측정에 부적합해졌고 SWE-Bench Pro 보고를 권장한다고 밝혔다.',
            },
            {
                title: 'princeton-nlp/SWE-bench_Verified',
                source: 'Hugging Face',
                url: 'https://huggingface.co/datasets/princeton-nlp/SWE-bench_Verified',
                note: 'Hugging Face dataset card는 Verified가 500-row test split이며 repo, instance_id, base_commit, patch, test_patch, problem_statement, FAIL_TO_PASS, PASS_TO_PASS, difficulty 컬럼을 가진다고 보여준다.',
            },
            {
                title: 'SWE-bench datasets guide',
                source: 'SWE-bench',
                url: 'https://www.swebench.com/SWE-bench/guides/datasets/',
                note: '공식 데이터셋 가이드는 SWE-bench, Lite, Verified, Multimodal 등 subset의 역할과 Hugging Face 로딩 방법을 정리한다.',
            },
            {
                title: 'SWE-bench: Can Language Models Resolve Real-World GitHub Issues?',
                source: 'arXiv',
                url: 'https://arxiv.org/abs/2310.06770',
                note: '원본 SWE-bench 논문은 12개 Python repository의 2,294개 실제 GitHub issue와 PR 기반 patch 평가 형식을 설명한다.',
            },
        ],
        visuals: [
            {
                src: 'https://filecdn.minimax.chat/public/58eca777-e31f-448a-9823-e2220e49b426.png',
                alt: 'SWE-bench Verified human validation filter 다이어그램',
                caption: 'SWE-bench Verified는 원본 SWE-bench test sample을 전문 개발자가 검수해 문제 설명과 테스트가 더 명확한 500개 subset으로 줄인 것이다.',
                sourceTitle: 'Introducing SWE-bench Verified',
                sourceUrl: 'https://openai.com/index/introducing-swe-bench-verified/',
                referenceIndex: 1,
                featured: true,
            },
            {
                src: 'https://www.tbench.ai/og/ascii-logo-dark-1200x630.png',
                alt: 'SWE-bench Verified patch 평가 흐름 다이어그램',
                caption: '평가는 issue text와 codebase만 보고 patch를 만들고, 숨겨진 fail-to-pass와 pass-to-pass 테스트를 모두 통과하는지 확인한다.',
                sourceTitle: 'Terminal-Bench raster image',
                sourceUrl: 'https://www.tbench.ai/',
                referenceIndex: 3,
                featured: true,
            },
            {
                src: 'https://filecdn.minimax.chat/public/58eca777-e31f-448a-9823-e2220e49b426.png',
                alt: 'SWE-bench Verified contamination과 test flaw 리스크 다이어그램',
                caption: 'Verified는 legacy baseline으로는 유용하지만, frontier 모델 비교에서는 공개 gold patch 노출과 residual test flaw 가능성을 별도로 표시해야 한다.',
                sourceTitle: 'SWE-bench leaderboard raster image',
                sourceUrl: 'https://www.swebench.com/',
                referenceIndex: 2,
            },
        ],
        demoIdeas: [
            'legacy baseline 데모: 과거 논문/모델 카드의 Verified 점수를 같은 harness 조건으로 표시하고 Pro 또는 내부 held-out 점수와 분리한다.',
            'test flaw 분석 데모: 실패 사례를 narrow test, wide test, 환경 재현 실패, 실제 모델 한계로 나누어 리뷰한다.',
            '오염 점검 데모: task ID, gold patch, problem statement가 공개되어 있는지와 모델이 관련 세부 정보를 재현하는지 기록한다.',
            '내부 전환 데모: Verified 형식을 참고하되 사내 repo에서 비공개 issue, 비공개 test, 독립 검수 절차가 있는 held-out set을 만든다.',
        ],
    },
    {
        slug: 'terminal-bench-2',
        name: 'Terminal-Bench 2.0',
        category: '코딩 / 소프트웨어 엔지니어링',
        categoryId: 'coding',
        badge: 'terminal agent',
        summary: 'Terminal-Bench는 sandbox terminal에서 파일, 셸, 서비스, 테스트를 다루는 agent task benchmark다.',
        measures: '명령 실행, 파일 수정, dependency 처리, 테스트 디버깅, 장기 작업 완수 능력을 본다.',
        evaluation: '격리된 환경에서 agent가 명령을 수행한 뒤 task별 verifier가 성공 여부를 판단한다.',
        bestFor: '개발/운영 자동화 agent의 실제 터미널 사용 능력을 비교할 때 적합하다.',
        watchouts: '모델보다 agent runtime의 tool policy, timeout, shell affordance가 점수에 강하게 작용한다.',
        internalEval: '배포, 로그 분석, 테스트 복구 같은 내부 CLI workflow를 task로 만든다.',
        sourceTitle: 'SWE-bench leaderboard raster image',
        sourceUrl: 'https://www.swebench.com/',
        plainMeaning: '터미널 사용 코딩 평가입니다. 모델이 셸 명령, 파일 수정, 테스트 실행을 실제 작업처럼 다룰 수 있는지 봅니다.',
        exampleDatasetUrl: '/assets/benchmark-examples/terminal-bench-2-sample.json',
        exampleDatasetNote: 'Terminal-Bench/Hugging Face 컬럼 구조를 반영한 웹 표시용 합성 예시입니다.',
        exampleRows: [
            {
                id: 'terminal-bench-2-demo-network-001',
                task: '터미널 시스템 복구',
                input: 'instruction/base_description: 컨테이너 안에서 네트워크 설정이 깨져 있으며 curl로 외부 URL을 확인할 수 있게 고치라는 작업 지시',
                expected: '검증 스크립트가 curl, 설정 파일, 서비스 상태를 확인해 통과',
                scoring: '컨테이너에서 agent 실행 후 tests/test_outputs.py 또는 run-tests.sh가 성공하면 pass',
                questionFormat: 'Q는 자연어 instruction + task.yaml + 초기 파일/서비스/환경으로 제공됩니다.',
                answerFormat: 'A는 텍스트 답변이 아니라 컨테이너 내부 최종 상태와 테스트 통과 여부입니다.',
                extraColumns: 'task_id, archive, task_yaml, difficulty, tags, category, max_agent_timeout_sec, max_test_timeout_sec, tar_sha256, archive_bytes, n_files, generated_at',
            },
            {
                id: 'terminal-bench-2-demo-build-002',
                task: '빌드/컴파일 작업',
                input: 'instruction/base_description: 소스와 빌드 도구가 들어 있는 컨테이너에서 커널, C/C++ 프로젝트, 패키지 등을 빌드하라는 작업 지시',
                expected: '지정된 바이너리, 이미지, 로그, artifact가 생성되고 검증 테스트가 통과',
                scoring: '빌드 성공만 보지 않고 artifact 위치, 실행 결과, 회귀 테스트를 함께 확인합니다.',
                questionFormat: 'Q는 task_id별 archive 안의 task.yaml, Dockerfile 또는 docker-compose.yaml, task-deps 파일로 구성됩니다.',
                answerFormat: 'A는 agent가 남긴 파일 변경, 명령 실행 결과, 테스트 통과 상태입니다.',
                extraColumns: 'difficulty와 tags로 system-administration, compilation, security, data-science 같은 작업군을 분리합니다.',
            },
            {
                id: 'terminal-bench-2-demo-data-003',
                task: '데이터 처리/모델 작업',
                input: 'instruction/base_description: 데이터 압축, 모델 학습, 파일 변환, 로그 요약처럼 여러 명령과 스크립트가 필요한 작업 지시',
                expected: '요구된 출력 파일, 모델 파일, 변환 결과, 정확도 또는 형식 검증 결과',
                scoring: '최종 파일과 테스트 결과가 모두 맞아야 하며 timeout, dependency, flaky test 실패를 따로 기록합니다.',
                questionFormat: 'Q는 자연어 목표와 입력 데이터, 제한 시간, 사용할 수 있는 터미널 환경으로 구성됩니다.',
                answerFormat: 'A는 채점 스크립트가 확인할 수 있는 재현 가능한 최종 산출물입니다.',
                extraColumns: 'archive는 gzipped task directory이고 tar_sha256은 재현성과 무결성 확인에 쓰입니다.',
            },
        ],
        references: [
            {
                title: 'Terminal-Bench official site',
                source: 'Terminal-Bench',
                url: 'https://www.tbench.ai/',
                note: '공식 사이트는 Terminal-Bench가 Harbor-native terminal agent benchmark이며 terminal-bench@2.0은 89개 고품질 task로 구성된다고 설명한다.',
            },
            {
                title: 'Terminal-Bench: Benchmarking Agents on Hard, Realistic Tasks in Command Line Interfaces',
                source: 'arXiv',
                url: 'https://arxiv.org/abs/2601.11868',
                note: '논문은 Terminal-Bench 2.0을 실제 workflow에서 영감을 받은 89개 hard terminal task로 설명하고 각 task가 고유 환경, human-written solution, verification test를 가진다고 정리한다.',
            },
            {
                title: 'harbor-framework/terminal-bench',
                source: 'GitHub',
                url: 'https://github.com/harbor-framework/terminal-bench',
                note: 'GitHub 저장소는 task dataset과 execution harness, sandboxed terminal environment, tb CLI 실행 구조를 제공한다.',
            },
            {
                title: 'terminal-bench@2.0',
                source: 'Harbor Registry',
                url: 'https://www.harborframework.com/registry/terminal-bench/2.0',
                note: 'Harbor registry는 terminal-bench@2.0 실행 명령과 89개 task 목록, task별 실행 id를 보여준다.',
            },
            {
                title: 'ia03/terminal-bench',
                source: 'Hugging Face',
                url: 'https://huggingface.co/datasets/ia03/terminal-bench',
                note: 'Hugging Face dataset card는 task_id, archive, task_yaml, difficulty, tags, category, base_description, timeout, tar_sha256 등 재현용 컬럼을 설명한다.',
            },
        ],
        visuals: [
            {
                src: 'https://filecdn.minimax.chat/public/58eca777-e31f-448a-9823-e2220e49b426.png',
                alt: 'Terminal-Bench 2.0 평가 루프 다이어그램',
                caption: 'Terminal-Bench 2.0은 자연어 task를 컨테이너에 넣고 agent가 터미널 명령을 수행한 뒤 hidden test와 로그로 성공 여부를 판정한다.',
                sourceTitle: 'Terminal-Bench paper',
                sourceUrl: 'https://arxiv.org/abs/2601.11868',
                referenceIndex: 2,
                featured: true,
            },
            {
                src: 'https://www.tbench.ai/og/ascii-logo-dark-1200x630.png',
                alt: 'Terminal-Bench 2.0 데이터셋 컬럼 다이어그램',
                caption: '예시 데이터셋은 Q/A 텍스트만이 아니라 task archive, YAML, category, timeout, hash까지 함께 보존해야 재현 가능한 terminal 평가가 된다.',
                sourceTitle: 'Terminal-Bench raster image',
                sourceUrl: 'https://www.tbench.ai/',
                referenceIndex: 5,
                featured: true,
            },
            {
                src: 'https://filecdn.minimax.chat/public/58eca777-e31f-448a-9823-e2220e49b426.png',
                alt: 'Terminal-Bench 2.0 실행 조건 분리 다이어그램',
                caption: 'Terminal-Bench 2.0 점수는 모델만의 점수가 아니라 agent scaffold, timeout, Docker 환경, retry/pass@k 조건까지 포함한 실행 계약의 결과다.',
                sourceTitle: 'SWE-bench leaderboard raster image',
                sourceUrl: 'https://www.swebench.com/',
                referenceIndex: 1,
            },
        ],
        demoIdeas: [
            '터미널 재생 데모: task instruction, 실행 명령, 파일 변경, 테스트 로그, 최종 pass/fail을 시간순으로 보여준다.',
            '실패 유형 데모: dependency 설치 실패, 잘못된 경로, timeout, 테스트 실패, artifact 누락을 분리해 표시한다.',
            'agent scaffold 비교 데모: 같은 모델을 다른 terminal agent scaffold로 실행해 tool policy와 timeout 차이를 별도 series로 비교한다.',
            '내부 전환 데모: 사내 배포, 로그 분석, 인증서 생성, 데이터 변환 작업을 Docker task와 pytest verifier가 있는 Terminal-Bench식 held-out 세트로 만든다.',
        ],
    },
    {
        slug: 'livecodebench',
        name: 'LiveCodeBench',
        category: '코딩 / 소프트웨어 엔지니어링',
        categoryId: 'coding',
        badge: 'live coding',
        summary: 'LiveCodeBench는 LeetCode, AtCoder, Codeforces의 신규 contest 문제를 계속 수집해 코드 LLM의 최신 알고리즘 문제 해결력을 평가하는 benchmark다.',
        measures: '문제 설명 이해, 알고리즘 설계, 코드 생성뿐 아니라 self-repair, code execution, test output prediction처럼 코드 관련 하위 능력을 나누어 본다.',
        evaluation: 'code generation은 hidden test 실행 결과로 pass@1/pass@5를 계산하고, release_version과 contest_date로 기간별 문제 세트를 나누어 contamination 가능성을 줄인다.',
        bestFor: '코딩 모델이 공개된 오래된 HumanEval류 문제가 아니라 최근 contest 문제를 얼마나 안정적으로 푸는지 비교할 때 적합하다.',
        watchouts: 'release_version, code_generation_lite와 원본 test 수 차이, timeout, sampling n, start/end date를 맞추지 않으면 같은 LiveCodeBench 점수도 직접 비교하기 어렵다.',
        internalEval: '사내 알고리즘/데이터 처리 문제를 월별로 추가하고 문제 공개일, hidden test, 실패 로그를 보존해 LiveCodeBench식 기간 분리 평가로 운영한다.',
        sourceTitle: 'SWE-bench leaderboard raster image',
        sourceUrl: 'https://www.swebench.com/',
        plainMeaning: '최신 알고리즘 코딩 평가입니다. 모델이 새로 나온 온라인 코딩 대회 문제를 읽고, 실행 가능한 코드를 만들어 숨겨진 테스트를 통과하는지 봅니다.',
        exampleDatasetUrl: '/assets/benchmark-examples/livecodebench-sample.json',
        exampleDatasetNote: 'LiveCodeBench 공식 저장소와 Hugging Face 데이터셋 컬럼을 반영한 웹 표시용 요약 예시입니다.',
        exampleRows: [
            {
                id: 'livecodebench-codegen-demo-001',
                task: 'code generation',
                input: 'question_title/question_content: contest 문제 설명, 입력/출력 형식, 제약, 예제 테스트를 포함합니다.',
                expected: '모델이 생성한 완전한 프로그램 또는 starter_code를 채운 함수 코드',
                scoring: 'runner가 public/private test를 실행하고 pass@1 또는 pass@5로 집계합니다.',
                questionFormat: 'Q는 question_title, question_content, starter_code, public_test_cases로 구성됩니다.',
                answerFormat: 'A는 자연어 설명이 아니라 실행 가능한 code string 또는 code_list입니다.',
                extraColumns: 'platform, question_id, contest_id, contest_date, difficulty, private_test_cases, metadata, release_version',
            },
            {
                id: 'livecodebench-test-output-demo-002',
                task: 'test output prediction',
                input: 'question_content와 starter_code, function_name, 그리고 특정 test input이 주어집니다.',
                expected: '해당 테스트 입력을 실행했을 때 나와야 하는 output 문자열',
                scoring: '모델이 예측한 출력이 dataset의 test.output과 정확히 맞는지 확인합니다.',
                questionFormat: 'Q는 문제 설명 + 함수 시그니처 + 하나의 test JSON입니다.',
                answerFormat: 'A는 코드가 아니라 예측 출력값입니다.',
                extraColumns: 'question_id, contest_id, test_id, contest_date, function_name, difficulty, test',
            },
            {
                id: 'livecodebench-execution-demo-003',
                task: 'code execution',
                input: 'function_name, 이미 작성된 code, 그리고 함수 호출 형태의 input이 주어집니다.',
                expected: '주어진 코드를 실제로 따라 실행했을 때의 return value 또는 출력',
                scoring: '예상 output과 일치하는지 보며, 긴 실행 추론은 numsteps로 난도를 가늠합니다.',
                questionFormat: 'Q는 code snippet + input 호출식입니다.',
                answerFormat: 'A는 실행 결과 output입니다.',
                extraColumns: 'id, function_name, code, input, output, numsteps, problem_id',
            },
        ],
        references: [
            {
                title: 'LiveCodeBench official site',
                source: 'LiveCodeBench',
                url: 'https://livecodebench.github.io/',
                note: '공식 사이트는 LiveCodeBench가 LeetCode, AtCoder, Codeforces contest 문제를 지속 수집하고 code generation 외 self-repair, code execution, test output prediction을 다룬다고 설명한다.',
            },
            {
                title: 'LiveCodeBench: Holistic and Contamination Free Evaluation of Large Language Models for Code',
                source: 'arXiv',
                url: 'https://arxiv.org/abs/2403.07974',
                note: '논문은 기존 HumanEval, MBPP만으로는 최신 코드 LLM 평가가 부족하며, 문제 공개일을 기준으로 contamination을 줄이는 평가를 제안한다.',
            },
            {
                title: 'LiveCodeBench/LiveCodeBench',
                source: 'GitHub',
                url: 'https://github.com/LiveCodeBench/LiveCodeBench',
                note: '공식 저장소는 release_v1부터 release_v6까지의 버전, scenario별 실행 명령, pass@1/pass@5 계산, start_date/end_date 기반 score 산출 방법을 문서화한다.',
            },
            {
                title: 'livecodebench/code_generation_lite',
                source: 'Hugging Face',
                url: 'https://huggingface.co/datasets/livecodebench/code_generation_lite',
                note: 'code_generation_lite는 question_title, question_content, platform, question_id, contest_id, contest_date, starter_code, difficulty, public/private test, metadata 컬럼을 가진다.',
            },
            {
                title: 'livecodebench/execution',
                source: 'Hugging Face',
                url: 'https://huggingface.co/datasets/livecodebench/execution',
                note: 'execution dataset은 function_name, code, input, output, numsteps, problem_id를 제공해 모델이 코드를 따라 실행하고 결과를 예측하는지 본다.',
            },
            {
                title: 'livecodebench/test_generation',
                source: 'Hugging Face',
                url: 'https://huggingface.co/datasets/livecodebench/test_generation',
                note: 'test_generation dataset은 question_content, starter_code, function_name, test JSON, contest_date를 제공해 특정 입력의 출력 예측을 평가한다.',
            },
            {
                title: 'LiveCodeBench ERRATA',
                source: 'GitHub',
                url: 'https://github.com/LiveCodeBench/LiveCodeBench/blob/main/ERRATA.md',
                note: 'ERRATA는 multiple valid outputs, interactive problem, erroneous test case처럼 자동 채점에서 noise가 생길 수 있는 문제 유형을 별도로 기록한다.',
            },
        ],
        visuals: [
            {
                src: 'https://filecdn.minimax.chat/public/58eca777-e31f-448a-9823-e2220e49b426.png',
                alt: 'LiveCodeBench 시간 분리 평가 흐름 다이어그램',
                caption: 'LiveCodeBench는 contest_date와 release_version을 보존해 모델 학습 cutoff 이후 문제만 골라 보는 식으로 contamination 위험을 줄인다.',
                sourceTitle: 'LiveCodeBench GitHub',
                sourceUrl: 'https://github.com/LiveCodeBench/LiveCodeBench',
                referenceIndex: 3,
                featured: true,
            },
            {
                src: 'https://www.tbench.ai/og/ascii-logo-dark-1200x630.png',
                alt: 'LiveCodeBench 평가 시나리오 분해 다이어그램',
                caption: '하나의 코딩 점수로 뭉개지 않고 code generation, self-repair, test output prediction, code execution을 나누면 모델이 약한 지점을 더 분명히 볼 수 있다.',
                sourceTitle: 'Terminal-Bench raster image',
                sourceUrl: 'https://www.tbench.ai/',
                referenceIndex: 1,
                featured: true,
            },
            {
                src: 'https://filecdn.minimax.chat/public/58eca777-e31f-448a-9823-e2220e49b426.png',
                alt: 'LiveCodeBench 데이터셋 컬럼 구조 다이어그램',
                caption: '예시 데이터셋은 문제 본문만이 아니라 platform, contest_id, contest_date, difficulty, hidden test, metadata까지 함께 보존해야 재현 가능한 비교가 된다.',
                sourceTitle: 'SWE-bench leaderboard raster image',
                sourceUrl: 'https://www.swebench.com/',
                referenceIndex: 4,
            },
        ],
        demoIdeas: [
            'release window 데모: 모델 학습 cutoff를 입력하면 그 날짜 이후 contest_date 문제만 필터링해 pass@1/pass@5를 다시 계산한다.',
            '시나리오 분해 데모: 같은 모델의 code generation, self-repair, test output prediction, code execution 점수를 나란히 보여주고 약한 하위 능력을 표시한다.',
            '문항 리플레이 데모: question_content, public test, 모델 code_list, hidden test 결과, timeout/error 로그를 순서대로 펼쳐 실패 원인을 확인한다.',
            'lite vs full test 데모: code_generation_lite와 원본 test 수 차이를 표시하고 속도, 비용, 점수 안정성의 trade-off를 비교한다.',
            '내부 전환 데모: 사내 신규 알고리즘 문제에 contest_date, difficulty, hidden test, metadata를 붙여 LiveCodeBench와 같은 기간별 held-out 세트로 관리한다.',
        ],
    },
    {
        slug: 'humaneval',
        name: 'HumanEval',
        category: '코딩 / 소프트웨어 엔지니어링',
        categoryId: 'coding',
        badge: 'saturated baseline',
        summary: 'HumanEval은 OpenAI가 공개한 164개 손작성 Python 함수 구현 문제로, docstring을 읽고 함수 본문을 생성하는 기본 코드 생성 benchmark다.',
        measures: '함수 시그니처와 docstring, 예시 호출을 보고 짧은 Python 함수를 완성해 unit test를 통과하는지 본다.',
        evaluation: '모델이 task별 completion을 여러 개 생성하면 prompt와 합쳐 실행하고, check(candidate) 테스트 통과 여부로 pass@1/pass@10/pass@100 같은 pass@k를 계산한다.',
        bestFor: '짧은 Python 함수 생성 능력의 역사적 baseline이나 회귀 테스트를 빠르게 확인할 때 유용하다.',
        watchouts: '문제가 164개로 작고 공개된 지 오래되어 최신 상위 모델에서는 포화와 데이터 오염 가능성이 크며, repo 수정이나 멀티파일 agent 능력은 거의 보지 못한다.',
        internalEval: 'HumanEval 형식을 smoke test로만 두고, 사내 함수/SDK/edge case를 추가한 비공개 unit-test 세트와 SWE-bench류 repo 작업 평가를 함께 둔다.',
        sourceTitle: 'Evaluating Large Language Models Trained on Code',
        sourceUrl: 'https://arxiv.org/abs/2107.03374',
        plainMeaning: '짧은 Python 함수 구현 평가입니다. 모델이 함수 설명과 예시를 보고 함수 본문만 작성해서 숨겨진 unit test를 통과하는지 봅니다.',
        exampleDatasetUrl: '/assets/benchmark-examples/humaneval-sample.json',
        exampleDatasetNote: 'OpenAI HumanEval 원본 JSONL schema와 평가 sample 형식을 반영한 웹 표시용 요약 예시입니다.',
        exampleRows: [
            {
                id: 'humaneval-demo-close-elements-001',
                task: 'Python 함수 구현',
                input: 'prompt: from typing import List와 has_close_elements(numbers, threshold) 함수 시그니처, docstring, doctest 예시가 포함됩니다.',
                expected: 'completion: 두 수의 거리 차이를 검사해 threshold보다 가까운 쌍이 있으면 True를 반환하는 함수 본문',
                scoring: 'prompt + completion을 실행한 뒤 check(candidate)의 assert를 모두 통과하면 pass로 계산합니다.',
                questionFormat: 'Q는 task_id와 prompt입니다. prompt 안에는 import, 함수 시그니처, docstring, 예시 호출이 들어갑니다.',
                answerFormat: 'A는 전체 파일이 아니라 prompt 뒤에 붙일 Python completion 코드입니다.',
                extraColumns: 'canonical_solution, test, entry_point가 함께 제공되지만 모델 입력에는 보통 prompt만 사용합니다.',
            },
            {
                id: 'humaneval-demo-paren-groups-002',
                task: '문자열/자료구조 함수 구현',
                input: 'prompt: separate_paren_groups(paren_string) 함수와 괄호 그룹을 분리하라는 docstring, 예시 출력이 주어집니다.',
                expected: 'completion: depth counter로 balanced group을 잘라 List[str]을 반환하는 함수 본문',
                scoring: '여러 assert가 있는 check(candidate) 테스트를 모두 통과해야 pass입니다.',
                questionFormat: 'Q는 Python 타입 힌트, 함수명, 자연어 명세, doctest 스타일 예시가 섞인 코드 prompt입니다.',
                answerFormat: 'A는 들여쓰기된 함수 내부 구현 코드입니다.',
                extraColumns: 'entry_point는 평가 harness가 candidate 함수명을 찾는 데 사용합니다.',
            },
            {
                id: 'humaneval-demo-sample-result-003',
                task: 'completion 평가 결과',
                input: 'samples.jsonl: {"task_id": "HumanEval/0", "completion": "..."} 형식으로 모델 생성 결과를 저장합니다.',
                expected: 'samples.jsonl_results.jsonl: completion별 passed와 result("passed", "timed out", "failed")',
                scoring: '여러 completion 중 통과한 개수를 이용해 unbiased pass@k estimator로 집계합니다.',
                questionFormat: 'Q는 problem file의 task_id와 prompt입니다.',
                answerFormat: 'A는 completion only이며 prompt를 다시 포함하지 않습니다.',
                extraColumns: '평가 결과 파일에는 passed, result, completion_id 같은 실행 결과 메타데이터가 붙습니다.',
            },
        ],
        references: [
            {
                title: 'openai/human-eval',
                source: 'GitHub',
                url: 'https://github.com/openai/human-eval',
                note: '공식 저장소는 HumanEval evaluation harness와 JSONL sample 형식, task_id와 completion만 담은 model output format, pass@k 평가 명령을 설명한다.',
            },
            {
                title: 'Evaluating Large Language Models Trained on Code',
                source: 'arXiv',
                url: 'https://arxiv.org/abs/2107.03374',
                note: 'Codex 논문은 HumanEval을 docstring에서 Python 프로그램을 합성하는 functional correctness 평가 세트로 소개하고 pass@k 반복 샘플링 결과를 보고한다.',
            },
            {
                title: 'HumanEval.jsonl.gz',
                source: 'OpenAI GitHub data',
                url: 'https://github.com/openai/human-eval/blob/master/data/HumanEval.jsonl.gz',
                note: '원본 데이터는 task_id, prompt, entry_point, canonical_solution, test 컬럼을 가진 164개 Python 함수 구현 문제로 구성된다.',
            },
            {
                title: 'openai/openai_humaneval',
                source: 'Hugging Face',
                url: 'https://huggingface.co/datasets/openai/openai_humaneval',
                note: 'Hugging Face viewer도 test split의 feature를 task_id, prompt, canonical_solution, test, entry_point로 노출한다.',
            },
            {
                title: 'example_problem.jsonl',
                source: 'OpenAI GitHub data',
                url: 'https://github.com/openai/human-eval/blob/master/data/example_problem.jsonl',
                note: '예시 problem file은 평가 문제 한 줄이 prompt, canonical_solution, test, entry_point를 어떻게 포함하는지 보여준다.',
            },
            {
                title: 'example_samples.jsonl',
                source: 'OpenAI GitHub data',
                url: 'https://github.com/openai/human-eval/blob/master/data/example_samples.jsonl',
                note: '예시 sample file은 모델 출력이 task_id와 completion만 포함하며, 안전하지 않은 코드도 들어올 수 있어 sandbox 실행이 필요함을 보여준다.',
            },
        ],
        visuals: [
            {
                kind: 'text',
                src: 'humaneval-eval-flow',
                alt: 'HumanEval 평가 흐름 설명',
                eyebrow: '평가 흐름',
                title: 'prompt -> completion -> unit test',
                steps: [
                    'problem row의 prompt는 Python import, 함수 시그니처, docstring, 예시 호출을 담는다.',
                    '모델은 prompt 뒤에 붙일 completion 코드만 생성한다.',
                    '평가 harness는 prompt와 completion을 합친 candidate를 실행한다.',
                    'check(candidate)의 assert 통과 여부를 모아 pass@k를 계산한다.',
                ],
                caption: 'HumanEval은 prompt 뒤에 모델 completion을 붙여 candidate 함수를 만들고, check(candidate) unit test를 통과하는지로 pass@k를 계산한다.',
                sourceTitle: 'openai/human-eval',
                sourceUrl: 'https://github.com/openai/human-eval',
                referenceIndex: 1,
            },
            {
                kind: 'text',
                src: 'humaneval-dataset-schema',
                alt: 'HumanEval 데이터셋 컬럼 설명',
                eyebrow: '데이터 구조',
                title: '입력 컬럼과 채점 컬럼을 분리해서 본다',
                points: [
                    '모델 입력: task_id, prompt',
                    '정답/채점 자료: canonical_solution, test, entry_point',
                    '모델 제출: task_id, completion',
                    '실행 결과: passed, result, completion_id',
                ],
                caption: '한 row에는 모델이 보는 prompt와, 채점에 쓰는 canonical_solution, test, entry_point가 같이 들어 있어 Q/A와 채점 자료를 분리해 해석해야 한다.',
                sourceTitle: 'HumanEval.jsonl.gz',
                sourceUrl: 'https://github.com/openai/human-eval/blob/master/data/HumanEval.jsonl.gz',
                referenceIndex: 3,
            },
            {
                kind: 'text',
                src: 'humaneval-limitations',
                alt: 'HumanEval 활용 한계 설명',
                eyebrow: '해석 한계',
                title: '짧은 함수 smoke test로만 쓴다',
                points: [
                    '164개 공개 Python 함수 문제라 최신 모델에는 포화와 오염 가능성이 크다.',
                    'repo 수정, 멀티파일 변경, 터미널 작업, 장기 agent 계획은 거의 보지 못한다.',
                    '내부 평가는 사내 함수/SDK와 hidden unit test로 별도 구성한다.',
                ],
                caption: 'HumanEval은 빠른 smoke test로는 여전히 유용하지만, 공개된 짧은 Python 함수 세트라 최신 코딩 agent의 repo 수정 능력까지 설명하지는 못한다.',
                sourceTitle: 'Evaluating Large Language Models Trained on Code',
                sourceUrl: 'https://arxiv.org/abs/2107.03374',
                referenceIndex: 2,
            },
        ],
        demoIdeas: [
            'prompt-completion 결합 데모: prompt, 모델 completion, 합쳐진 candidate 코드, check(candidate) 결과를 나란히 보여준다.',
            'pass@k 데모: 같은 task에 completion 1개, 10개, 100개를 생성했을 때 pass@k가 왜 달라지는지 시각화한다.',
            '실패 유형 데모: syntax error, wrong return, timeout, unsafe side effect, edge case fail을 결과 로그에서 분리한다.',
            '포화 확인 데모: HumanEval 점수가 높은 모델도 LiveCodeBench, SWE-bench, Terminal-Bench에서 어떤 차이가 나는지 함께 보여준다.',
            '내부 전환 데모: 사내 SDK 함수 docstring과 hidden unit test를 사용해 HumanEval식 비공개 smoke test를 만든다.',
        ],
    },
    {
        slug: 'mbpp',
        name: 'MBPP',
        category: '코딩 / 소프트웨어 엔지니어링',
        categoryId: 'coding',
        badge: 'basic programming',
        summary: 'MBPP는 Google Research가 공개한 약 1,000개 crowd-sourced Python 기초 프로그래밍 문제로, 자연어 설명을 읽고 짧은 Python 함수를 작성하는 benchmark다.',
        measures: '입문자 수준의 문제 설명 이해, 함수 구현, 기본 자료구조, 표준 라이브러리 사용, assert 기반 테스트 통과 능력을 본다.',
        evaluation: '모델이 생성한 Python code를 task별 test_list, test_setup_code, 필요 시 challenge_test_list와 함께 실행해 assert를 통과하는지 계산한다.',
        bestFor: 'HumanEval보다 더 많은 기초 Python 문제로 소형 모델, fine-tune 모델, 코드 생성 회귀 테스트를 빠르게 확인할 때 적합하다.',
        watchouts: '문제가 짧고 공개되어 있으며 full과 sanitized 버전의 컬럼과 split이 달라 점수를 비교할 때 config, task_id 범위, prompt에 테스트를 포함했는지 확인해야 한다.',
        internalEval: '사내 기초 함수, 데이터 변환, 문자열 처리, SDK helper 문제를 MBPP식 prompt/code/test_list 구조로 만들고 비공개 edge test를 추가한다.',
        sourceTitle: 'Terminal-Bench raster image',
        sourceUrl: 'https://www.tbench.ai/',
        plainMeaning: '기초 Python 함수 구현 평가입니다. 모델이 짧은 자연어 지시를 보고 함수를 만들고, 몇 개의 assert 테스트를 통과하는지 확인합니다.',
        exampleDatasetUrl: '/assets/benchmark-examples/mbpp-sample.json',
        exampleDatasetNote: 'Google Research MBPP 원본/full/sanitized 컬럼 구조를 반영한 웹 표시용 요약 예시입니다.',
        exampleRows: [
            {
                id: 'mbpp-demo-full-min-cost-001',
                task: 'full MBPP 함수 구현',
                input: 'text: 주어진 cost matrix에서 (0,0)부터 (m,n)까지의 minimum cost path를 구하는 함수를 작성하라는 자연어 문제',
                expected: 'code: min_cost(cost, m, n) 함수 구현',
                scoring: 'test_setup_code를 먼저 실행한 뒤 test_list의 assert 3개와 challenge_test_list를 통과하면 pass입니다.',
                questionFormat: 'Q는 text와 test_list입니다. 일부 prompt 구성에서는 테스트 assert도 함께 모델에게 보여줍니다.',
                answerFormat: 'A는 실행 가능한 Python code입니다. 보통 함수 정의와 필요한 helper/import를 포함합니다.',
                extraColumns: 'task_id, test_setup_code, test_list, challenge_test_list가 원본 full row에 들어갑니다.',
            },
            {
                id: 'mbpp-demo-sanitized-similar-002',
                task: 'sanitized MBPP 함수 구현',
                input: 'prompt: 두 tuple/list에서 shared elements를 찾는 함수를 작성하라는 정제된 자연어 설명',
                expected: 'code: similar_elements(test_tup1, test_tup2) 함수 구현',
                scoring: 'sanitized test_list는 set 비교처럼 원본 모호성을 줄인 assert를 포함합니다.',
                questionFormat: 'Q는 prompt, test_imports, test_list로 구성됩니다.',
                answerFormat: 'A는 prompt에 맞는 Python 함수 구현 코드입니다.',
                extraColumns: 'source_file, task_id, prompt, code, test_imports, test_list가 sanitized row의 핵심 컬럼입니다.',
            },
            {
                id: 'mbpp-demo-split-eval-003',
                task: 'split과 평가 조건',
                input: 'task_id 11-510은 test, 1-10은 few-shot prompt, 511-600은 validation, 601-974는 train으로 쓰는 정책',
                expected: '모델 completion을 test split 문제에만 실행하고 train/prompt task를 평가에 섞지 않는 결과',
                scoring: '같은 task_id 범위, 같은 full/sanitized config, 같은 prompt template에서 통과율이나 pass@k를 비교합니다.',
                questionFormat: 'Q는 split에 따라 선택한 text/prompt row와 테스트 assert입니다.',
                answerFormat: 'A는 completion code이며 평가 harness가 test_list를 붙여 실행합니다.',
                extraColumns: 'config(full/sanitized), split(train/test/validation/prompt), task_id range, prompt template을 결과와 함께 기록해야 합니다.',
            },
        ],
        references: [
            {
                title: 'Mostly Basic Python Problems Dataset',
                source: 'Google Research GitHub',
                url: 'https://github.com/google-research/google-research/tree/master/mbpp',
                note: '공식 README는 MBPP가 약 1,000개 crowd-sourced Python 문제이며 각 문제가 task description, code solution, 3 automated test cases를 포함한다고 설명한다.',
            },
            {
                title: 'Program Synthesis with Large Language Models',
                source: 'arXiv',
                url: 'https://arxiv.org/abs/2108.07732',
                note: '논문은 MBPP를 entry-level programmer가 풀 수 있는 974개 Python task로 소개하고, 자연어 설명에서 짧은 프로그램을 합성하는 능력을 평가한다.',
            },
            {
                title: 'mbpp.jsonl',
                source: 'Google Research data',
                url: 'https://github.com/google-research/google-research/blob/master/mbpp/mbpp.jsonl',
                note: '원본 full JSONL은 text, code, task_id, test_setup_code, test_list, challenge_test_list 컬럼을 가진다.',
            },
            {
                title: 'sanitized-mbpp.json',
                source: 'Google Research data',
                url: 'https://github.com/google-research/google-research/blob/master/mbpp/sanitized-mbpp.json',
                note: 'hand-verified sanitized subset은 source_file, task_id, prompt, code, test_imports, test_list 구조를 가지며 확인한 원본 파일 기준 427개 row를 포함한다.',
            },
            {
                title: 'google-research-datasets/mbpp',
                source: 'Hugging Face',
                url: 'https://huggingface.co/datasets/google-research-datasets/mbpp',
                note: 'Hugging Face dataset viewer는 full과 sanitized config, train/test/validation/prompt split, 각 config의 feature schema를 제공한다.',
            },
            {
                title: 'MBPP split policy',
                source: 'Google Research README',
                url: 'https://github.com/google-research/google-research/tree/master/mbpp#evaluation-details',
                note: '공식 README는 task_id 11-510을 test, 1-10을 few-shot prompt, 511-600을 validation, 601-974를 train으로 사용하는 평가 split을 명시한다.',
            },
        ],
        visuals: [
            {
                src: 'https://www.tbench.ai/og/ascii-logo-dark-1200x630.png',
                alt: 'MBPP 평가 흐름 다이어그램',
                caption: 'MBPP는 자연어 문제를 Python code로 바꾸고, test_list의 assert를 실행해 함수가 실제로 동작하는지 확인한다.',
                sourceTitle: 'MBPP GitHub',
                sourceUrl: 'https://github.com/google-research/google-research/tree/master/mbpp',
                referenceIndex: 1,
                featured: true,
            },
            {
                src: 'https://filecdn.minimax.chat/public/58eca777-e31f-448a-9823-e2220e49b426.png',
                alt: 'MBPP full과 sanitized 데이터셋 컬럼 다이어그램',
                caption: 'full row는 text/code/test_setup_code/challenge_test_list를 포함하고, sanitized row는 prompt/test_imports 중심으로 정제되어 config를 섞어 비교하면 안 된다.',
                sourceTitle: 'SWE-bench leaderboard raster image',
                sourceUrl: 'https://www.swebench.com/',
                referenceIndex: 5,
                featured: true,
            },
            {
                src: 'https://www.tbench.ai/og/ascii-logo-dark-1200x630.png',
                alt: 'MBPP task_id split 정책 다이어그램',
                caption: 'MBPP 점수는 task_id split 정책을 지켜야 한다. few-shot prompt, validation, train 문제를 test 점수에 섞으면 basic coding baseline 의미가 흐려진다.',
                sourceTitle: 'Terminal-Bench raster image',
                sourceUrl: 'https://www.tbench.ai/',
                referenceIndex: 6,
            },
        ],
        demoIdeas: [
            'assert 리플레이 데모: prompt, 모델 code, test_setup_code, test_list 실행 결과를 한 줄씩 보여주고 어떤 assert에서 실패했는지 표시한다.',
            'full/sanitized 토글 데모: 같은 task_id가 full과 sanitized에서 어떻게 다르게 정제되었는지 prompt, code, test 컬럼을 비교한다.',
            'split guard 데모: 선택한 task_id가 prompt/test/validation/train 중 어디에 속하는지 즉시 표시해 평가 누수를 막는다.',
            'edge test 데모: 기본 test_list 3개는 통과하지만 challenge_test_list나 내부 hidden test에서 깨지는 예시를 보여준다.',
            '내부 전환 데모: 사내 helper 함수 문제를 text/code/test_list/challenge_test_list로 작성하고 공개 MBPP 점수와 별도 regression suite로 관리한다.',
        ],
    },
    {
        slug: 'multiple',
        name: 'MultiPL-E',
        category: '코딩 / 소프트웨어 엔지니어링',
        categoryId: 'coding',
        badge: 'multilingual code',
        summary: 'MultiPL-E는 HumanEval과 MBPP 계열 문제를 여러 프로그래밍 언어로 확장한 benchmark다.',
        measures: 'Python 외 JavaScript, Java, C++, Rust 등 다양한 언어의 코드 생성 능력을 본다.',
        evaluation: '언어별 테스트 harness를 통해 pass@k를 계산한다.',
        bestFor: '여러 언어를 지원해야 하는 코딩 assistant의 언어별 편차를 볼 때 좋다.',
        watchouts: '문제 유형이 짧은 함수 구현 중심이라 대규모 프로젝트 문맥은 별도 평가가 필요하다.',
        internalEval: '사내 주력 언어와 framework별 scaffold, test runner를 포함한 문제를 만든다.',
        sourceTitle: 'Humanity\'s Last Exam difficulty image',
        sourceUrl: 'https://www.lastexam.ai/',
    },
    {
        slug: 'bigcodebench',
        name: 'BigCodeBench',
        category: '코딩 / 소프트웨어 엔지니어링',
        categoryId: 'coding',
        badge: 'practical Python',
        summary: 'BigCodeBench는 라이브러리 사용과 현실적인 Python 함수 구현을 포함한 코드 생성 평가다.',
        measures: '표준/서드파티 라이브러리 활용, 명세 이해, edge case 처리 능력을 본다.',
        evaluation: '문제별 unit test와 execution result를 기준으로 채점한다.',
        bestFor: '간단한 알고리즘보다 실용 Python task 성능을 비교할 때 적합하다.',
        watchouts: '여전히 함수 단위 평가라 multi-file software engineering은 SWE-bench류와 함께 봐야 한다.',
        internalEval: '사내 라이브러리와 SDK를 활용하는 함수형 task를 별도 구축한다.',
        sourceTitle: 'BigCodeBench',
        sourceUrl: 'https://bigcode-bench.github.io/',
    },
    {
        slug: 'cruxeval',
        name: 'CRUXEval',
        category: '코딩 / 소프트웨어 엔지니어링',
        categoryId: 'coding',
        badge: 'code reasoning',
        summary: 'CRUXEval은 코드 실행 결과 예측과 역추론으로 모델의 코드 이해 능력을 평가한다.',
        measures: '코드를 읽고 출력 값을 예측하거나, 목표 출력에 맞는 입력을 찾는 reasoning을 본다.',
        evaluation: '정답 일치율로 채점하며, 생성 코드보다 code semantics 이해에 초점을 둔다.',
        bestFor: '코드 리뷰, 디버깅, 테스트 분석처럼 코드를 읽는 능력이 중요한 모델 비교에 좋다.',
        watchouts: '직접 patch를 작성하는 능력은 별도로 평가해야 한다.',
        internalEval: '실제 버그 코드와 로그를 주고 원인/출력/수정 방향을 맞히는 task를 만든다.',
        sourceTitle: 'CRUXEval',
        sourceUrl: 'https://crux-eval.github.io/',
    },
    {
        slug: 'scicode',
        name: 'SciCode',
        category: '코딩 / 소프트웨어 엔지니어링',
        categoryId: 'coding',
        badge: 'scientific coding',
        summary: 'SciCode는 과학 계산과 코딩이 결합된 문제로 research coding 능력을 평가한다.',
        measures: '수학/과학 지식, numerical implementation, 라이브러리 사용, 검증 가능한 결과 생성을 본다.',
        evaluation: '문제별 코드 실행 결과와 테스트를 기준으로 성공 여부를 판단한다.',
        bestFor: '연구, 데이터 분석, scientific computing assistant 모델을 고를 때 유용하다.',
        watchouts: '웹 앱 개발이나 일반 repo 수정 능력과는 다른 축이다.',
        internalEval: '사내 분석 notebook, simulation, 데이터 처리 task를 재현 가능한 seed와 함께 만든다.',
        sourceTitle: 'SciCode',
        sourceUrl: 'https://scicode-bench.github.io/',
    },
    {
        slug: 'ccbench',
        name: 'CCBench',
        category: '코딩 / 소프트웨어 엔지니어링',
        categoryId: 'coding',
        badge: 'small repo agents',
        summary: 'CCBench는 작은 실제 codebase에서 CodeCrafters 방식 테스트로 coding agent task를 검증하는 benchmark다.',
        measures: '작은 repo 이해, 요구사항 구현, 단계별 테스트 통과, 에이전트 반복 수정 능력을 본다.',
        evaluation: '테스트 러너가 단계별 구현 결과를 검증해 task success를 계산한다.',
        bestFor: 'SWE-bench보다 작지만 repo 문맥이 있는 coding agent 평가가 필요할 때 적합하다.',
        watchouts: '작은 repo 중심이라 대형 monorepo나 복잡한 dependency 문제는 별도 평가가 필요하다.',
        internalEval: '사내 starter project와 acceptance test를 묶어 작은 repo 과제를 만든다.',
        sourceTitle: 'CCBench',
        sourceUrl: 'https://ccbench.org/',
    },
    {
        slug: 'mle-bench',
        name: 'MLE-bench',
        category: '코딩 / 소프트웨어 엔지니어링',
        categoryId: 'coding',
        badge: 'ML engineering',
        summary: 'MLE-bench는 Kaggle competition 기반으로 ML engineering end-to-end 능력을 평가한다.',
        measures: '데이터 준비, feature engineering, 모델 학습, 실험 반복, 제출 파일 생성 능력을 본다.',
        evaluation: 'competition metric과 제출 결과를 기준으로 bronze/silver/gold 수준 성과를 비교한다.',
        bestFor: 'ML 실험 자동화 agent나 데이터 과학 업무 모델을 평가할 때 적합하다.',
        watchouts: '실행 비용과 시간이 크고, 강한 baseline 코드가 있으면 agent scaffold 영향이 커진다.',
        internalEval: '사내 데이터셋의 baseline notebook과 hidden test metric을 만들어 비교한다.',
        sourceTitle: 'OpenAI MLE-bench',
        sourceUrl: 'https://openai.com/index/mle-bench/',
    },
    {
        slug: 'browsecomp',
        name: 'BrowseComp',
        category: '에이전트 / 웹 / 컴퓨터 사용',
        categoryId: 'agent-web',
        badge: 'hard web search',
        summary: 'BrowseComp는 웹에서 찾기 어려운 단답 정보를 탐색해 찾아내는 browsing agent benchmark다.',
        measures: '검색 질의 설계, 여러 페이지 탐색, 근거 해석, 최종 단답 정확도를 본다.',
        evaluation: '정답이 명확한 질문에 대해 agent가 제출한 최종 답을 채점한다.',
        bestFor: '웹 리서치 agent의 검색 전략과 탐색 지속성을 비교할 때 유용하다.',
        watchouts: '웹 상태가 변할 수 있고, 브라우저 도구 품질과 search API가 점수에 영향을 준다.',
        internalEval: '실제 조사 업무에서 정답과 출처가 확인된 단답형 질문을 만들고 citation을 함께 채점한다.',
        sourceTitle: 'OpenAI BrowseComp',
        sourceUrl: 'https://openai.com/index/browsecomp/',
    },
    {
        slug: 'gaia',
        name: 'GAIA',
        category: '에이전트 / 웹 / 컴퓨터 사용',
        categoryId: 'agent-web',
        badge: 'general assistant',
        summary: 'GAIA는 reasoning, multimodal, web browsing, tool-use가 필요한 일반 assistant task benchmark다.',
        measures: '사람에게는 비교적 쉬우나 에이전트에게 어려운 정보 수집, 계산, 도구 사용, 파일 해석을 본다.',
        evaluation: '최종 답 exact match와 task별 채점 기준으로 성공률을 계산한다.',
        bestFor: '범용 agent가 실제 비서형 문제를 얼마나 처리하는지 보는 대표 지표다.',
        watchouts: 'agent scaffold와 도구 접근성이 강하게 작용하므로 모델 이름만으로 비교하면 안 된다.',
        internalEval: '사내 assistant 요청 로그를 정답 검증 가능한 multi-step task로 재구성한다.',
        sourceTitle: 'GAIA paper',
        sourceUrl: 'https://arxiv.org/abs/2311.12983',
    },
    {
        slug: 'webarena',
        name: 'WebArena',
        category: '에이전트 / 웹 / 컴퓨터 사용',
        categoryId: 'agent-web',
        badge: 'web navigation',
        summary: 'WebArena는 self-hosted 웹 환경에서 쇼핑, forum, GitLab, CMS 같은 작업을 수행하는 benchmark다.',
        measures: '웹 navigation, form 작성, 검색, 상태 변경, 여러 페이지를 넘나드는 작업 완수를 본다.',
        evaluation: '환경의 최종 상태나 task-specific verifier로 성공 여부를 판단한다.',
        bestFor: '브라우저 조작형 agent와 웹 업무 자동화 모델을 평가할 때 적합하다.',
        watchouts: '환경이 self-hosted라 실제 외부 웹의 불안정성과 anti-bot 문제는 덜 반영된다.',
        internalEval: '사내 admin, CRM, issue tracker에서 read-only와 write task를 분리해 재현 환경을 만든다.',
        sourceTitle: 'WebArena paper',
        sourceUrl: 'https://arxiv.org/abs/2307.13854',
    },
    {
        slug: 'visualwebarena',
        name: 'VisualWebArena',
        category: '에이전트 / 웹 / 컴퓨터 사용',
        categoryId: 'agent-web',
        badge: 'visual web',
        summary: 'VisualWebArena는 화면 시각 정보가 필요한 웹 작업으로 multimodal web agent를 평가한다.',
        measures: 'DOM 텍스트만으로 풀기 어려운 UI grounding, 이미지 이해, 화면 기반 action selection을 본다.',
        evaluation: '웹 환경 task를 수행한 뒤 최종 상태와 정답을 검증한다.',
        bestFor: '스크린샷 기반 브라우저 조작이나 시각 요소가 많은 SaaS 자동화 평가에 유용하다.',
        watchouts: '화면 해상도, browser rendering, accessibility tree 품질이 결과에 영향을 준다.',
        internalEval: '실제 화면 캡처와 DOM을 함께 제공하는 UI task를 만들고 클릭 좌표 오류를 기록한다.',
        sourceTitle: 'VisualWebArena paper',
        sourceUrl: 'https://arxiv.org/abs/2401.13649',
    },
    {
        slug: 'mind2web',
        name: 'Mind2Web',
        category: '에이전트 / 웹 / 컴퓨터 사용',
        categoryId: 'agent-web',
        badge: 'web actions',
        summary: 'Mind2Web은 실제 웹사이트의 action sequence를 기반으로 웹 agent의 generalization을 평가한다.',
        measures: '웹 페이지 요소 선택, action grounding, cross-domain generalization, task planning을 본다.',
        evaluation: '정답 action과 예측 action의 element/action matching score를 계산한다.',
        bestFor: '웹 UI action prediction 모델이나 browser agent의 grounding 능력을 비교할 때 좋다.',
        watchouts: '오프라인 trajectory 중심이라 실제 실행 후 복구 능력은 WebArena류와 함께 봐야 한다.',
        internalEval: '사내 웹 앱의 사용 로그를 action sequence로 익명화하고 unseen screen에서 평가한다.',
        sourceTitle: 'Mind2Web',
        sourceUrl: 'https://osu-nlp-group.github.io/Mind2Web/',
    },
    {
        slug: 'osworld',
        name: 'OSWorld',
        category: '에이전트 / 웹 / 컴퓨터 사용',
        categoryId: 'agent-web',
        badge: 'desktop control',
        summary: 'OSWorld는 실제 운영체제에서 데스크톱 앱, 파일, 브라우저를 조작하는 computer-use benchmark다.',
        measures: 'GUI grounding, mouse/keyboard action, cross-app workflow, 파일 작업, 브라우저 작업을 본다.',
        evaluation: '실행 환경에서 task를 수행한 뒤 최종 상태를 verifier가 판단한다.',
        bestFor: 'computer-use agent나 데스크톱 자동화 모델 비교에 핵심적인 지표다.',
        watchouts: '환경 설정, 앱 버전, 화면 크기, action latency가 점수에 큰 영향을 준다.',
        internalEval: '사내 데스크톱/브라우저 workflow를 VM snapshot과 verifier로 재현한다.',
        sourceTitle: 'OSWorld paper',
        sourceUrl: 'https://arxiv.org/abs/2404.07972',
    },
    {
        slug: 'androidworld',
        name: 'AndroidWorld',
        category: '에이전트 / 웹 / 컴퓨터 사용',
        categoryId: 'agent-web',
        badge: 'mobile agent',
        summary: 'AndroidWorld는 Android 앱 환경에서 모바일 agent가 여러 앱 task를 수행하는지 평가한다.',
        measures: '모바일 UI 탐색, touch action, 앱 간 이동, 설정 변경, 정보 입력/검색을 본다.',
        evaluation: 'Android emulator나 device 환경에서 task success를 verifier로 판단한다.',
        bestFor: '모바일 앱 자동화 agent나 스마트폰 assistant 성능을 비교할 때 적합하다.',
        watchouts: '앱 버전, 로그인 상태, 권한 팝업, emulator 차이가 결과에 영향을 준다.',
        internalEval: '사내 모바일 앱의 핵심 사용자 journey를 테스트 계정과 초기 상태로 고정한다.',
        sourceTitle: 'AndroidWorld GitHub',
        sourceUrl: 'https://github.com/google-research/android_world',
    },
    {
        slug: 'workarena',
        name: 'WorkArena',
        category: '에이전트 / 웹 / 컴퓨터 사용',
        categoryId: 'agent-web',
        badge: 'enterprise web',
        summary: 'WorkArena는 업무용 웹 앱 workflow에서 enterprise-style agent task를 평가한다.',
        measures: '업무 SaaS 화면에서 record 조회, form 작성, 상태 변경, 절차 준수를 본다.',
        evaluation: 'task completion과 최종 시스템 상태를 기준으로 성공 여부를 계산한다.',
        bestFor: '기업 내부 업무 자동화 agent를 설계할 때 WebArena보다 업무 맥락이 가까운 기준이 된다.',
        watchouts: '특정 플랫폼과 workflow에 편향될 수 있어 사내 도구와 별도 mapping이 필요하다.',
        internalEval: 'CRM, ERP, helpdesk 같은 실제 업무 도구의 sandbox workflow를 만든다.',
        sourceTitle: 'WorkArena GitHub',
        sourceUrl: 'https://github.com/ServiceNow/WorkArena',
    },
    {
        slug: 'theagentcompany',
        name: 'TheAgentCompany',
        category: '에이전트 / 웹 / 컴퓨터 사용',
        categoryId: 'agent-web',
        badge: 'simulated company',
        summary: 'TheAgentCompany는 가상의 소프트웨어 회사에서 agent가 디지털 업무를 수행하는 능력을 평가한다.',
        measures: '웹, 코드, 문서, 커뮤니케이션, 계획 실행을 엮은 복합 office workflow를 본다.',
        evaluation: '업무별 성공 조건과 최종 artifact 또는 시스템 상태를 기준으로 채점한다.',
        bestFor: '단일 웹 task가 아니라 여러 도구를 넘나드는 디지털 worker 능력을 볼 때 유용하다.',
        watchouts: '환경 구성이 복잡하고 실제 회사 도구와 차이가 있어 내부 재현 세트가 필요하다.',
        internalEval: '사내 role별 반복 업무를 작은 company simulation task로 만들고 권한 경계를 둔다.',
        sourceTitle: 'TheAgentCompany',
        sourceUrl: 'https://the-agent-company.com/',
    },
    {
        slug: 'agentbench',
        name: 'AgentBench',
        category: '에이전트 / 웹 / 컴퓨터 사용',
        categoryId: 'agent-web',
        badge: 'multi-environment',
        summary: 'AgentBench는 OS, DB, KG, 웹 쇼핑/브라우징 등 여러 환경의 agent task를 묶은 benchmark다.',
        measures: '다양한 환경에서 planning, tool use, interaction, task completion 능력을 본다.',
        evaluation: '환경별 성공 기준을 정하고 총점 또는 영역별 점수로 비교한다.',
        bestFor: 'agent benchmark의 넓은 카테고리를 빠르게 훑고 baseline을 잡을 때 좋다.',
        watchouts: '초기 benchmark라 최신 browser/computer-use 환경 충실도와는 차이가 있다.',
        internalEval: '내부 agent eval도 DB, 웹, CLI, 문서 등 환경별 scorecard로 분리한다.',
        sourceTitle: 'AgentBench GitHub',
        sourceUrl: 'https://github.com/THUDM/AgentBench',
    },
    {
        slug: 'miniwob-plus-plus',
        name: 'MiniWoB++',
        category: '에이전트 / 웹 / 컴퓨터 사용',
        categoryId: 'agent-web',
        badge: 'UI control',
        summary: 'MiniWoB++는 작은 웹 UI task에서 클릭, 입력, 선택 같은 browser control을 평가한다.',
        measures: '단순 UI 조작, element grounding, 짧은 action sequence, instruction following을 본다.',
        evaluation: '미니 웹 환경에서 task success와 action sequence 품질을 측정한다.',
        bestFor: '브라우저 agent의 기본 조작 능력과 RL/ imitation learning baseline을 볼 때 유용하다.',
        watchouts: '현실 웹 복잡성은 낮아 실제 SaaS workflow는 WebArena류와 같이 봐야 한다.',
        internalEval: '사내 UI 컴포넌트별 최소 조작 task를 만들어 regression smoke test로 둔다.',
        sourceTitle: 'MiniWoB++ GitHub',
        sourceUrl: 'https://github.com/Farama-Foundation/miniwob-plusplus',
    },
    {
        slug: 'crab',
        name: 'CRAB',
        category: '에이전트 / 웹 / 컴퓨터 사용',
        categoryId: 'agent-web',
        badge: 'cross-platform',
        summary: 'CRAB은 Ubuntu와 Android 등 cross-platform 환경에서 agent task 수행력을 평가한다.',
        measures: '서로 다른 OS와 앱 환경에서 화면 이해, action planning, task transfer를 본다.',
        evaluation: '환경별 task verifier로 성공 여부와 trajectory를 평가한다.',
        bestFor: 'desktop과 mobile을 모두 다루는 범용 computer-use agent 비교에 적합하다.',
        watchouts: '플랫폼별 환경 구성과 action abstraction 차이를 통제해야 한다.',
        internalEval: '동일 업무를 desktop, mobile, web variant로 만들어 플랫폼별 실패 유형을 비교한다.',
        sourceTitle: 'CRAB',
        sourceUrl: 'https://crab.camel-ai.org/',
    },
    {
        slug: 'bfcl',
        name: 'BFCL',
        category: '툴 호출 / MCP',
        categoryId: 'tool-use',
        badge: 'function calling',
        summary: 'Berkeley Function-Calling Leaderboard는 함수 선택과 인자 구성 등 tool calling 능력을 평가한다.',
        measures: 'single/multiple function call, parallel call, REST API, relevance detection, argument accuracy를 본다.',
        evaluation: '정답 함수와 인자에 대한 AST/semantic match를 통해 점수를 계산한다.',
        bestFor: 'API agent나 함수 호출 지원 모델을 고를 때 가장 먼저 볼 만한 지표다.',
        watchouts: '실제 업무의 긴 multi-turn 상태 관리와 tool side effect는 tau-bench류와 같이 봐야 한다.',
        internalEval: '사내 API schema로 함수 선택, 권한 오류, optional argument, multi-call task를 만든다.',
        sourceTitle: 'BFCL',
        sourceUrl: 'https://sky.cs.berkeley.edu/project/berkeley-function-calling-leaderboard/',
    },
    {
        slug: 'tau-bench',
        name: 'tau-bench',
        category: '툴 호출 / MCP',
        categoryId: 'tool-use',
        badge: 'tool-agent-user',
        summary: 'tau-bench는 simulated user와 대화하며 정책과 API 도구를 따라 목표 DB 상태를 만드는 benchmark다.',
        measures: 'multi-turn 대화, 정책 준수, API 호출, 사용자와의 clarification, 최종 상태 달성을 본다.',
        evaluation: '목표 DB 상태 도달 여부와 pass^k 같은 반복 안정성 지표를 사용한다.',
        bestFor: '고객지원, 예약, 주문 변경 같은 tool-use agent의 실전 안정성을 평가하기 좋다.',
        watchouts: 'domain policy와 simulator 품질에 따라 난도가 크게 달라진다.',
        internalEval: '고객지원 로그에서 정책, API, 목표 상태를 추출해 simulator 기반 eval을 만든다.',
        sourceTitle: 'tau-bench paper',
        sourceUrl: 'https://arxiv.org/abs/2406.12045',
    },
    {
        slug: 'tau2-bench',
        name: 'tau2-bench',
        category: '툴 호출 / MCP',
        categoryId: 'tool-use',
        badge: 'multi-turn tools',
        summary: 'tau2-bench는 tau-bench 계열을 더 복잡한 multi-turn tool-use workflow로 확장한 benchmark다.',
        measures: '정책 기반 대화, 사용자 목표 변경, 여러 API 호출, 장기 상태 관리 능력을 본다.',
        evaluation: 'simulated user와 상호작용한 뒤 최종 상태와 정책 위반 여부를 평가한다.',
        bestFor: '현실 customer support workflow에 가까운 agent 안정성을 볼 때 유용하다.',
        watchouts: '모델 성능뿐 아니라 tool wrapper, memory, retry 정책의 영향이 크다.',
        internalEval: '복수 intent와 예외 케이스가 있는 support scenario를 pass^k로 평가한다.',
        sourceTitle: 'tau2-bench GitHub',
        sourceUrl: 'https://github.com/sierra-research/tau2-bench',
    },
    {
        slug: 'mcp-atlas',
        name: 'MCP-Atlas',
        category: '툴 호출 / MCP',
        categoryId: 'tool-use',
        badge: 'real MCP servers',
        summary: 'MCP-Atlas는 실제 MCP server들을 활용해 tool discovery와 multi-tool orchestration을 평가한다.',
        measures: '사용 가능한 tool 파악, 적절한 server 선택, tool chaining, 중간 결과 해석을 본다.',
        evaluation: 'MCP 환경에서 task를 실행하고 최종 답 또는 상태를 검증한다.',
        bestFor: 'MCP 기반 agent platform에서 모델과 tool description 품질을 비교할 때 적합하다.',
        watchouts: 'server 목록과 tool schema가 바뀌면 점수가 달라질 수 있다.',
        internalEval: '사내 MCP server를 sandbox로 복제하고 권한/오류/부분 실패 시나리오를 포함한다.',
        sourceTitle: 'MCP-Atlas paper',
        sourceUrl: 'https://arxiv.org/abs/2509.23238',
    },
    {
        slug: 'mcpmark',
        name: 'MCPMark',
        category: '툴 호출 / MCP',
        categoryId: 'tool-use',
        badge: 'MCP stress test',
        summary: 'MCPMark는 MCP 사용을 stress-test하는 sandbox 기반 재현 가능 benchmark다.',
        measures: 'MCP server 선택, tool call 정확성, long-horizon task, 재현 가능한 실패 유형을 본다.',
        evaluation: 'sandbox task의 성공 여부와 trajectory를 기록해 모델별 차이를 비교한다.',
        bestFor: 'MCP agent를 실제 운영하기 전 tool orchestration 안정성을 검증하는 데 적합하다.',
        watchouts: '새로운 benchmark이므로 다른 tool-use 지표와 함께 cross-check해야 한다.',
        internalEval: '운영 MCP 서버별 critical path task를 MCPMark식 sandbox로 분리한다.',
        sourceTitle: 'MCPMark',
        sourceUrl: 'https://mcpmark.ai/',
    },
    {
        slug: 'toolathlon',
        name: 'Toolathlon',
        category: '툴 호출 / MCP',
        categoryId: 'tool-use',
        badge: 'tool decathlon',
        summary: 'Toolathlon은 다양한 도구와 realistic environment에서 tool-use task completion을 평가한다.',
        measures: '도구 발견, 호출 순서 계획, 오류 복구, pass@k, turn 수, 최종 성공률을 본다.',
        evaluation: 'task별 environment에서 도구를 사용해 목표를 달성했는지 검증한다.',
        bestFor: 'tool agent의 폭넓은 도구 사용 체력과 효율성을 비교할 때 좋다.',
        watchouts: '사용 가능한 tool set과 agent loop 구현 차이를 명확히 기록해야 한다.',
        internalEval: '사내 도구 묶음을 난이도별 decathlon task로 구성하고 turn/cost를 같이 측정한다.',
        sourceTitle: 'Toolathlon',
        sourceUrl: 'https://toolathlon.xyz/',
    },
    {
        slug: 'toolbench',
        name: 'ToolBench',
        category: '툴 호출 / MCP',
        categoryId: 'tool-use',
        badge: 'API tools',
        summary: 'ToolBench는 API/tool instruction following과 tool planning 능력을 평가하는 초기 대표 benchmark다.',
        measures: '사용자 지시를 API 호출 계획으로 바꾸고 여러 tool을 조합하는 능력을 본다.',
        evaluation: 'tool call trajectory와 최종 응답 품질을 기준으로 평가한다.',
        bestFor: 'tool learning, API planning 연구의 baseline으로 참고하기 좋다.',
        watchouts: '초기 데이터셋이라 최신 function calling API와 운영형 agent의 요구사항은 별도 확인이 필요하다.',
        internalEval: '사내 API 문서와 실제 권한 제약을 반영한 tool planning task를 만든다.',
        sourceTitle: 'ToolBench GitHub',
        sourceUrl: 'https://github.com/OpenBMB/ToolBench',
    },
    {
        slug: 'api-bank',
        name: 'API-Bank',
        category: '툴 호출 / MCP',
        categoryId: 'tool-use',
        badge: 'API planning',
        summary: 'API-Bank는 모델이 API를 계획, 호출, 응답 해석까지 이어가는지 보는 tool-use benchmark다.',
        measures: 'API 선택, parameter 구성, multi-step API plan, API 응답 기반 후속 행동을 본다.',
        evaluation: '정답 API sequence와 최종 결과를 기준으로 평가한다.',
        bestFor: 'API-using LLM의 기본 planning 능력과 tool instruction following을 비교할 때 유용하다.',
        watchouts: '현대 function calling schema와 완전히 같지 않아 최신 BFCL류와 함께 봐야 한다.',
        internalEval: '사내 API dependency graph와 edge case를 반영한 sequence task를 만든다.',
        sourceTitle: 'API-Bank GitHub',
        sourceUrl: 'https://github.com/AlibabaResearch/DAMO-ConvAI/tree/main/api-bank',
    },
    {
        slug: 'gorilla-apibench',
        name: 'Gorilla APIBench',
        category: '툴 호출 / MCP',
        categoryId: 'tool-use',
        badge: 'API calls',
        summary: 'Gorilla APIBench는 API 문서 이해와 정확한 함수/API 호출 생성 능력을 평가한다.',
        measures: 'API retrieval, 함수명 선택, argument generation, documentation grounding을 본다.',
        evaluation: '정답 API call과 모델 출력의 구조적 일치 여부로 평가한다.',
        bestFor: '외부 API를 많이 쓰는 assistant의 문서 기반 tool call 능력을 볼 때 좋다.',
        watchouts: '긴 multi-turn 업무 처리와 권한/side effect 관리는 별도 평가가 필요하다.',
        internalEval: '사내 SDK 문서를 indexing하고 잘못된 함수명/인자 hallucination을 측정한다.',
        sourceTitle: 'Gorilla APIBench',
        sourceUrl: 'https://gorilla.cs.berkeley.edu/leaderboard.html',
    },
    {
        slug: 'mmmu',
        name: 'MMMU',
        category: '멀티모달 / 문서 / 비디오',
        categoryId: 'multimodal',
        badge: 'multimodal expert',
        summary: 'MMMU는 대학 수준의 다분야 multimodal 문제로 이미지와 전문 지식 추론을 함께 평가한다.',
        measures: '시각 자료 이해, 전문 지식, 수학/과학/인문 추론, multi-image reasoning을 본다.',
        evaluation: '객관식 또는 단답 정답률을 기준으로 multimodal model 성능을 비교한다.',
        bestFor: '이미지와 지식 추론이 결합된 범용 multimodal 모델 비교에 적합하다.',
        watchouts: '문서 OCR이나 GUI 조작처럼 특정 시각 업무는 별도 benchmark가 필요하다.',
        internalEval: '사내 이미지/도표/매뉴얼 기반 질문을 만들어 텍스트만으로 풀 수 없는 문제를 분리한다.',
        sourceTitle: 'MMMU',
        sourceUrl: 'https://mmmu-benchmark.github.io/',
    },
    {
        slug: 'mmmu-pro',
        name: 'MMMU-Pro',
        category: '멀티모달 / 문서 / 비디오',
        categoryId: 'multimodal',
        badge: 'harder MMMU',
        summary: 'MMMU-Pro는 MMMU보다 어려운 multimodal expert benchmark로 최신 모델 간 차이를 더 잘 드러낸다.',
        measures: '고난도 시각-전문 지식 추론, distractor 구분, 복합 이미지 이해를 본다.',
        evaluation: '문항별 정답률을 사용하며, MMMU보다 강화된 난도와 검증 기준을 사용한다.',
        bestFor: '상위 multimodal 모델이 MMMU에서 포화될 때 더 어려운 비교 축으로 활용한다.',
        watchouts: '문항 난도가 높아 일반 OCR/문서 QA 업무 성능과 직접 같지는 않다.',
        internalEval: '사내 전문 도면, 차트, 제품 이미지 기반 고난도 문제를 별도로 만든다.',
        sourceTitle: 'MMMU-Pro',
        sourceUrl: 'https://mmmu-benchmark.github.io/#mmmu-pro',
    },
    {
        slug: 'mathvista',
        name: 'MathVista',
        category: '멀티모달 / 문서 / 비디오',
        categoryId: 'multimodal',
        badge: 'visual math',
        summary: 'MathVista는 수학 도표, 그림, 표를 이해해 시각-수학 추론을 수행하는지 평가한다.',
        measures: 'geometry, chart, visual question answering, quantitative reasoning을 함께 본다.',
        evaluation: '문제별 정답 일치율을 기준으로 시각 정보와 수학 풀이 결합 능력을 채점한다.',
        bestFor: '차트나 그림이 포함된 수학/분석 질의 모델을 비교할 때 유용하다.',
        watchouts: '일반 문서 OCR이나 긴 보고서 이해와는 다른 시각-수학 중심 지표다.',
        internalEval: '사내 차트와 수치 보고서에서 계산이 필요한 질문을 만들고 계산 근거를 채점한다.',
        sourceTitle: 'MathVista',
        sourceUrl: 'https://mathvista.github.io/',
    },
    {
        slug: 'chartqa',
        name: 'ChartQA',
        category: '멀티모달 / 문서 / 비디오',
        categoryId: 'multimodal',
        badge: 'chart reasoning',
        summary: 'ChartQA는 차트와 그래프에서 값을 읽고 질문에 답하는 chart understanding benchmark다.',
        measures: '축/범례/값 읽기, 비교, 합산, 추세 파악, chart text 이해를 본다.',
        evaluation: '차트 이미지와 질문을 입력해 정답 텍스트 또는 숫자 일치율을 계산한다.',
        bestFor: 'BI dashboard, 보고서 차트, 시각 분석 assistant 평가에 적합하다.',
        watchouts: '복잡한 dashboard interaction이나 원본 데이터 접근 능력은 보지 못한다.',
        internalEval: '사내 KPI 차트와 실제 질문을 사용해 값 읽기와 계산 오류를 분리 기록한다.',
        sourceTitle: 'ChartQA GitHub',
        sourceUrl: 'https://github.com/vis-nlp/ChartQA',
    },
    {
        slug: 'docvqa',
        name: 'DocVQA',
        category: '멀티모달 / 문서 / 비디오',
        categoryId: 'multimodal',
        badge: 'document QA',
        summary: 'DocVQA는 문서 이미지에서 텍스트와 layout을 이해해 질의응답을 수행하는 benchmark다.',
        measures: 'OCR, layout understanding, table/form 이해, 문서 내 근거 탐색 능력을 본다.',
        evaluation: '문서 이미지와 질문을 입력하고 정답 문자열의 일치 또는 유사도를 평가한다.',
        bestFor: '스캔 문서, 계약서, 영수증, 양식 처리 모델을 비교할 때 유용하다.',
        watchouts: '문서 검색/RAG, 장문 요약, multi-document reasoning은 별도 평가가 필요하다.',
        internalEval: '사내 실제 문서 샘플을 마스킹하고 질문-정답-근거 박스를 함께 만든다.',
        sourceTitle: 'DocVQA',
        sourceUrl: 'https://www.docvqa.org/',
    },
    {
        slug: 'ocrbench',
        name: 'OCRBench',
        category: '멀티모달 / 문서 / 비디오',
        categoryId: 'multimodal',
        badge: 'OCR',
        summary: 'OCRBench는 scene text, document text, handwritten text 등 OCR 관련 능력을 폭넓게 평가한다.',
        measures: '텍스트 검출, 인식, 문서/표/장면 텍스트 이해, OCR 후 질의응답을 본다.',
        evaluation: '문항별 정답률 또는 OCR 결과 일치도를 사용한다.',
        bestFor: '문서/이미지에서 텍스트 추출이 중요한 multimodal 모델 비교에 적합하다.',
        watchouts: '순수 OCR 엔진과 VLM의 OCR+reasoning 능력을 구분해서 봐야 한다.',
        internalEval: '사내 문서 스캔 품질, 폰트, 도장, 손글씨를 포함한 OCR golden set을 만든다.',
        sourceTitle: 'OCRBench GitHub',
        sourceUrl: 'https://github.com/Yuliang-Liu/MultimodalOCR',
    },
    {
        slug: 'videomme',
        name: 'VideoMME',
        category: '멀티모달 / 문서 / 비디오',
        categoryId: 'multimodal',
        badge: 'video understanding',
        summary: 'VideoMME는 장단편 비디오 이해와 시간적 reasoning을 평가하는 benchmark다.',
        measures: '영상 내용 파악, temporal ordering, 이벤트 관계, 자막/음성/시각 정보 통합을 본다.',
        evaluation: '비디오와 질문을 입력해 객관식/단답 정답률을 계산한다.',
        bestFor: '영상 분석, 회의/교육 영상 이해, surveillance review 모델 비교에 유용하다.',
        watchouts: '비디오 길이, frame sampling, audio 사용 여부가 점수에 큰 영향을 준다.',
        internalEval: '사내 영상의 segment-level 질문과 timestamp 근거를 포함해 평가한다.',
        sourceTitle: 'VideoMME',
        sourceUrl: 'https://video-mme.github.io/',
    },
    {
        slug: 'screenspot',
        name: 'ScreenSpot',
        category: '멀티모달 / 문서 / 비디오',
        categoryId: 'multimodal',
        badge: 'GUI grounding',
        summary: 'ScreenSpot은 GUI 화면에서 지시된 요소를 찾아내는 visual grounding benchmark다.',
        measures: '화면 내 버튼/텍스트/아이콘 위치 인식, 자연어 지시와 UI element 연결을 본다.',
        evaluation: '모델이 예측한 bounding box나 좌표가 정답 요소와 일치하는지 평가한다.',
        bestFor: 'computer-use agent의 클릭 대상 인식과 UI grounding 품질을 확인할 때 적합하다.',
        watchouts: '요소를 찾는 능력만 보며 실제 클릭 후 상태 변경과 복구 능력은 별도다.',
        internalEval: '사내 앱 화면에서 자주 헷갈리는 버튼과 메뉴의 좌표/요소 matching 문제를 만든다.',
        sourceTitle: 'ScreenSpot / SeeClick',
        sourceUrl: 'https://github.com/njucckevin/SeeClick',
    },
    {
        slug: 'mmstar',
        name: 'MMStar',
        category: '멀티모달 / 문서 / 비디오',
        categoryId: 'multimodal',
        badge: 'multimodal reasoning',
        summary: 'MMStar는 시각 정보가 실제로 필요한 multimodal reasoning 문제로 shortcut을 줄이려는 benchmark다.',
        measures: 'vision-dependent reasoning, 시각-언어 결합, hallucination 억제, 문제 난도를 본다.',
        evaluation: '객관식 정답률을 사용하고 텍스트만으로 풀 수 있는 문제를 줄이는 데 초점을 둔다.',
        bestFor: 'VLM이 실제로 이미지를 보고 추론하는지 확인하는 데 유용하다.',
        watchouts: '특정 업무 문서나 UI 자동화와는 별도 benchmark를 병행해야 한다.',
        internalEval: '텍스트 설명만으로는 풀 수 없는 사내 이미지 질문을 분리해 blind text baseline과 비교한다.',
        sourceTitle: 'MMStar',
        sourceUrl: 'https://mmstar-benchmark.github.io/',
    },
    {
        slug: 'kmmlu',
        name: 'KMMLU',
        category: '한국어 / 안전 / 사실성',
        categoryId: 'korean-safety',
        badge: 'Korean MMLU',
        summary: 'KMMLU는 한국어 원문 시험 기반 35,030개 전문 객관식 문제로 한국어 지식과 문화 맥락을 평가한다.',
        measures: '한국어 독해, 한국 제도/문화/전문 분야 지식, 시험형 추론을 본다.',
        evaluation: 'multiple-choice accuracy로 모델별 한국어 지식 성능을 비교한다.',
        bestFor: '한국어 서비스 모델 후보를 영어 benchmark만으로 고르지 않기 위한 핵심 보조 지표다.',
        watchouts: '객관식 지식 문제이므로 대화 톤, 최신성, 업무 자동화 능력은 별도 평가가 필요하다.',
        internalEval: '한국어 고객 질문, 제도/정책, 회사 용어를 기반으로 자체 KMMLU형 문제를 만든다.',
        sourceTitle: 'KMMLU paper',
        sourceUrl: 'https://arxiv.org/abs/2402.11548',
    },
    {
        slug: 'hae-rae-bench',
        name: 'HAE-RAE Bench',
        category: '한국어 / 안전 / 사실성',
        categoryId: 'korean-safety',
        badge: 'Korean knowledge',
        summary: 'HAE-RAE Bench는 한국 문화와 언어 지식을 평가해 번역 benchmark의 한계를 보완한다.',
        measures: '한국어 어휘, 문법, 문화, 상식, 사회적 맥락 이해를 본다.',
        evaluation: '여러 Korean-specific task의 정답률 또는 점수를 합쳐 비교한다.',
        bestFor: '한국어와 한국 문화 맥락에 민감한 서비스 모델을 고를 때 유용하다.',
        watchouts: '일반 한국어 QA와 달리 task 구성이 문화/언어 지식에 집중되어 있다.',
        internalEval: '브랜드 톤, 한국어 존댓말, 지역/제도 표현까지 포함한 자체 언어 평가를 만든다.',
        sourceTitle: 'HAE-RAE Bench GitHub',
        sourceUrl: 'https://github.com/HAE-RAE/HAE-RAE-BENCH',
    },
    {
        slug: 'kobest',
        name: 'KoBEST',
        category: '한국어 / 안전 / 사실성',
        categoryId: 'korean-safety',
        badge: 'Korean NLU',
        summary: 'KoBEST는 한국어 NLU task 묶음으로 한국어 이해의 기본 체력을 평가한다.',
        measures: '상식 추론, 인과 관계, 문장 관계, 감성, 의미 이해 등 한국어 NLU 능력을 본다.',
        evaluation: 'task별 accuracy/F1 등 지정 metric을 사용한다.',
        bestFor: '한국어 모델의 기본 언어 이해 baseline을 확인할 때 적합하다.',
        watchouts: '생성형 답변 품질이나 agent tool use는 평가하지 않는다.',
        internalEval: '한국어 분류/판단/추론 task를 서비스 로그에서 만들어 regression test로 둔다.',
        sourceTitle: 'KoBEST GitHub',
        sourceUrl: 'https://github.com/SKT-AI/KoBEST',
    },
    {
        slug: 'kobalt',
        name: 'KoBALT',
        category: '한국어 / 안전 / 사실성',
        categoryId: 'korean-safety',
        badge: 'advanced Korean',
        summary: 'KoBALT는 고급 한국어 이해와 human preference 상관성을 강조한 benchmark다.',
        measures: '한국어 고난도 이해, 추론, 미묘한 표현 차이, 사용자 선호와의 상관성을 본다.',
        evaluation: '문항별 정답률과 benchmark 설계 기준에 따른 모델 점수를 비교한다.',
        bestFor: '한국어 상위 모델 간 차이를 더 민감하게 보려는 상황에 적합하다.',
        watchouts: '새로운 benchmark라 다른 한국어 eval과 함께 교차 검증해야 한다.',
        internalEval: '실제 한국어 사용자 선호 평가와 객관식 이해 문제를 함께 운영한다.',
        sourceTitle: 'KoBALT paper',
        sourceUrl: 'https://arxiv.org/abs/2505.16125',
    },
    {
        slug: 'kobbq',
        name: 'KoBBQ',
        category: '한국어 / 안전 / 사실성',
        categoryId: 'korean-safety',
        badge: 'Korean bias',
        summary: 'KoBBQ는 한국어 bias와 사회적 고정관념 관련 응답 안전성을 평가하는 benchmark다.',
        measures: '성별, 연령, 지역, 사회집단 등에 대한 편향적 판단과 ambiguity 대응을 본다.',
        evaluation: '질문 조건별 편향 선택 비율과 non-biased answer 비율을 비교한다.',
        bestFor: '한국어 서비스의 fairness와 bias risk를 확인할 때 중요하다.',
        watchouts: '안전성 전체를 대표하지 않으므로 유해 요청, 개인정보, 법률/의료 리스크와 별도 평가한다.',
        internalEval: '서비스 도메인의 민감 집단/표현을 포함한 한국어 safety suite를 만든다.',
        sourceTitle: 'KoBBQ GitHub',
        sourceUrl: 'https://github.com/naver-ai/kobbq',
    },
    {
        slug: 'click',
        name: 'CLIcK',
        category: '한국어 / 안전 / 사실성',
        categoryId: 'korean-safety',
        badge: 'culture and language',
        summary: 'CLIcK는 한국 문화와 언어 이해를 평가하는 Korean-specific benchmark 계열이다.',
        measures: '한국어 표현, 문화 상식, 지역/사회 맥락, 언어적 뉘앙스 이해를 본다.',
        evaluation: '문항별 정답률을 통해 한국 특화 지식과 언어 이해 능력을 비교한다.',
        bestFor: '번역된 영어 benchmark로 놓치기 쉬운 한국 문화 맥락을 확인할 때 유용하다.',
        watchouts: '최신 이슈나 회사 도메인 지식은 별도 업데이트가 필요하다.',
        internalEval: '한국 사용자 문의에서 문화/제도/표현 오해 사례를 모아 held-out task로 만든다.',
        sourceTitle: 'CLIcK paper',
        sourceUrl: 'https://arxiv.org/abs/2403.06412',
    },
    {
        slug: 'truthfulqa',
        name: 'TruthfulQA',
        category: '한국어 / 안전 / 사실성',
        categoryId: 'korean-safety',
        badge: 'truthfulness',
        summary: 'TruthfulQA는 흔한 오해나 false premise 질문에서 모델이 진실한 답을 내는지 평가한다.',
        measures: '거짓 전제 거부, 흔한 미신/오해 회피, 모르는 내용에 대한 과신 억제를 본다.',
        evaluation: 'truthfulness와 informativeness를 사람이 또는 자동 evaluator가 평가한다.',
        bestFor: 'hallucination과 misinformation risk를 점검하는 기본 안전 지표로 쓸 수 있다.',
        watchouts: '영어 중심이며 최신 사실성이나 한국어 도메인 사실성은 자체 eval이 필요하다.',
        internalEval: '제품/정책/법무 관련 false premise 질문을 만들어 정정 응답을 평가한다.',
        sourceTitle: 'TruthfulQA GitHub',
        sourceUrl: 'https://github.com/sylinrl/TruthfulQA',
    },
    {
        slug: 'realtoxicityprompts',
        name: 'RealToxicityPrompts',
        category: '한국어 / 안전 / 사실성',
        categoryId: 'korean-safety',
        badge: 'toxicity',
        summary: 'RealToxicityPrompts는 독성 completion을 유도하는 prompt에서 모델 안전성을 평가한다.',
        measures: '유해, 모욕, 혐오, 공격적 표현으로 이어질 가능성을 본다.',
        evaluation: 'prompt completion을 생성한 뒤 toxicity classifier나 score로 평가한다.',
        bestFor: '생성형 모델의 기본 toxicity risk와 decoding 설정 영향을 확인할 때 유용하다.',
        watchouts: '분류기 편향과 언어/문화 차이가 있어 한국어 안전성에는 별도 세트가 필요하다.',
        internalEval: '한국어 악성 사용자 입력과 브랜드 안전 기준을 반영해 completion risk를 측정한다.',
        sourceTitle: 'AI2 RealToxicityPrompts',
        sourceUrl: 'https://allenai.org/data/real-toxicity-prompts',
    },
    {
        slug: 'bbq',
        name: 'BBQ',
        category: '한국어 / 안전 / 사실성',
        categoryId: 'korean-safety',
        badge: 'bias',
        summary: 'BBQ는 사회적 bias와 ambiguity가 있는 질문에서 모델이 편향된 답을 내는지 평가한다.',
        measures: '성별, 인종, 종교, 국적 등 사회적 집단에 대한 stereotyped answer 경향을 본다.',
        evaluation: 'ambiguous/disambiguated context에서 bias score와 accuracy를 계산한다.',
        bestFor: '영어권 safety/bias baseline을 확인하고 모델 비교의 기본 리스크 지표로 쓰기 좋다.',
        watchouts: '한국어와 한국 사회 맥락은 KoBBQ 같은 별도 평가가 필요하다.',
        internalEval: '서비스 사용자군에 맞춘 편향 시나리오와 안전 응답 정책을 별도로 검증한다.',
        sourceTitle: 'BBQ GitHub',
        sourceUrl: 'https://github.com/nyu-mll/BBQ',
    },
    {
        slug: 'harmbench',
        name: 'HarmBench',
        category: '한국어 / 안전 / 사실성',
        categoryId: 'korean-safety',
        badge: 'safety',
        summary: 'HarmBench는 유해 요청 대응과 safety refusal 안정성을 평가하는 benchmark다.',
        measures: '유해 정보 요청, jailbreak, policy-violating instruction에 대한 거절과 안전한 대안 제시를 본다.',
        evaluation: '공격 prompt에 대한 모델 응답을 safety classifier나 judge로 평가한다.',
        bestFor: '공개 배포 전 모델의 safety hardening과 jailbreak regression을 확인할 때 적합하다.',
        watchouts: '정책별 허용/거부 경계가 달라 서비스 정책과 맞춰 재채점해야 한다.',
        internalEval: '사내 정책 taxonomy와 실제 abuse 로그를 기반으로 red-team suite를 만든다.',
        sourceTitle: 'HarmBench',
        sourceUrl: 'https://www.harmbench.org/',
    },
];

export const BENCHMARK_CATEGORIES = [
    { id: 'reasoning', title: '추론 / 지식 / 선호' },
    { id: 'coding', title: '코딩 / 소프트웨어 엔지니어링' },
    { id: 'agent-web', title: '에이전트 / 웹 / 컴퓨터 사용' },
    { id: 'tool-use', title: '툴 호출 / MCP' },
    { id: 'multimodal', title: '멀티모달 / 문서 / 비디오' },
    { id: 'korean-safety', title: '한국어 / 안전 / 사실성' },
];

export const BENCHMARK_CATEGORY_GUIDES: BenchmarkCategoryGuide[] = [
    {
        id: 'reasoning',
        title: '추론 / 지식 / 선호',
        scope: '폐쇄형 지식, 수학·과학 추론, 사용자 선호처럼 모델 단독 답변의 품질을 본다.',
        scoreUnit: '정답률, Elo/선호 점수, 과제별 평균처럼 답변 자체를 채점하는 단위가 많다.',
        decisionUse: '범용 모델 후보를 좁히고 reasoning mode나 프롬프트 전략의 효과를 빠르게 확인할 때 쓴다.',
        watchout: '대화 스타일, 도구 사용, 운영 비용을 직접 설명하지 않으므로 업무형 평가와 분리한다.',
    },
    {
        id: 'coding',
        title: '코딩 / 소프트웨어 엔지니어링',
        scope: '함수 구현부터 실제 저장소 이슈 해결까지 코드 작성, 수정, 테스트 통과 능력을 본다.',
        scoreUnit: 'pass@k, task success, 테스트 통과율처럼 실행 가능한 산출물을 기준으로 채점한다.',
        decisionUse: 'IDE agent, 코드 리뷰 보조, 리팩터링 자동화 모델을 고를 때 우선 확인한다.',
        watchout: '스캐폴드, 도구 예산, 재시도 정책이 점수를 크게 바꾸므로 모델 이름만 비교하지 않는다.',
    },
    {
        id: 'agent-web',
        title: '에이전트 / 웹 / 컴퓨터 사용',
        scope: '웹 탐색, 데스크톱·모바일 조작, 장기 계획처럼 환경 상태를 바꾸는 작업 성공률을 본다.',
        scoreUnit: '최종 답변 정확도, UI 상태 변경 완료, 단계별 action success를 함께 본다.',
        decisionUse: '리서치 agent, 브라우저 자동화, 사무 자동화처럼 툴과 환경이 있는 업무 후보를 좁힌다.',
        watchout: '브라우저, 계정, 화면 해상도, 관찰 방식이 달라지면 같은 모델도 다른 성능을 낸다.',
    },
    {
        id: 'tool-use',
        title: '툴 호출 / MCP',
        scope: '함수 선택, 인자 구성, 여러 도구 조합, API 상태 관리처럼 구조화된 호출 능력을 본다.',
        scoreUnit: '정확한 호출 여부, 목표 DB 상태, 호출 순서, 실패 복구 여부로 채점한다.',
        decisionUse: '사내 API, MCP server, customer support workflow에 연결할 모델을 비교할 때 쓴다.',
        watchout: '스키마 설명 품질과 정책 제약이 성능에 직접 영향을 주므로 실제 도구 사양으로 재검증한다.',
    },
    {
        id: 'multimodal',
        title: '멀티모달 / 문서 / 비디오',
        scope: '이미지, 문서 레이아웃, 차트, 화면, 비디오를 읽고 텍스트 답변으로 연결하는 능력을 본다.',
        scoreUnit: '객관식 정확도, OCR/grounding 정확도, 시각 근거 기반 답변 정확도를 사용한다.',
        decisionUse: '문서 QA, 차트 해석, 화면 이해, 비디오 요약처럼 시각 입력이 핵심인 업무를 선별한다.',
        watchout: '이미지 해상도, crop, OCR 전처리, 텍스트-only shortcut 여부를 확인해야 한다.',
    },
    {
        id: 'korean-safety',
        title: '한국어 / 안전 / 사실성',
        scope: '한국어 지식·문화 맥락, 편향, 독성, 사실성, 거절 안정성처럼 운영 리스크를 본다.',
        scoreUnit: '정답률, 편향/독성 발생률, 정책 위반률, factuality 분류를 함께 사용한다.',
        decisionUse: '한국어 서비스와 공개 사용자 대상 챗봇에서 기본 모델 후보를 걸러낼 때 필요하다.',
        watchout: '번역 벤치마크만으로는 한국 제도, 높임말, 문화 맥락, 안전 정책 품질을 대체할 수 없다.',
    },
];

export const BENCHMARK_CATEGORY_REFERENCES: Record<string, BenchmarkReference[]> = {
    reasoning: [
        {
            title: 'Humanity\'s Last Exam official site',
            source: 'CAIS / Scale AI',
            url: 'https://www.lastexam.ai/',
            note: 'HLE 공식 사이트는 기존 시험형 벤치마크와 frontier 모델 난도 비교를 시각 자료로 제공한다.',
        },
        {
            title: 'centerforaisafety/hle images',
            source: 'GitHub',
            url: 'https://github.com/centerforaisafety/hle/tree/main/images',
            note: 'HLE 공개 저장소는 분야 분포와 데이터셋 구조를 설명하는 이미지 자산을 제공한다.',
        },
    ],
    coding: [
        {
            title: 'SWE-bench',
            source: 'SWE-bench',
            url: 'https://www.swebench.com/',
            note: 'SWE-bench는 실제 GitHub 이슈 해결과 테스트 통과 기반의 소프트웨어 엔지니어링 벤치마크를 제공한다.',
        },
        {
            title: 'Terminal-Bench',
            source: 'Terminal-Bench',
            url: 'https://www.tbench.ai/',
            note: 'Terminal-Bench는 셸, 파일, 서비스, 테스트를 다루는 터미널 작업 벤치마크를 설명한다.',
        },
    ],
    'agent-web': [
        {
            title: 'WebArena',
            source: 'WebArena',
            url: 'https://webarena.dev/',
            note: 'WebArena는 self-hosted 웹 환경에서 agent가 실제 웹 작업을 수행하는 평가 환경을 제공한다.',
        },
        {
            title: 'OSWorld',
            source: 'OSWorld',
            url: 'https://os-world.github.io/',
            note: 'OSWorld는 실제 운영체제 앱과 파일, 브라우저를 조작하는 computer-use task 예시를 제공한다.',
        },
    ],
    'tool-use': [
        {
            title: 'Berkeley Function Calling Leaderboard',
            source: 'Berkeley / Gorilla',
            url: 'https://gorilla.cs.berkeley.edu/leaderboard.html',
            note: 'BFCL은 함수 선택, 인자 구성, 병렬 호출 같은 function calling 능력을 평가한다.',
        },
        {
            title: 'MCPMark',
            source: 'MCPMark',
            url: 'https://mcpmark.ai/',
            note: 'MCPMark는 실제 MCP 사용 흐름을 stress-test하는 benchmark와 시각 자료를 제공한다.',
        },
    ],
    multimodal: [
        {
            title: 'MMMU',
            source: 'MMMU Benchmark',
            url: 'https://mmmu-benchmark.github.io/',
            note: 'MMMU는 대학 수준 다분야 multimodal 문제의 구성과 통계를 공개한다.',
        },
        {
            title: 'MathVista',
            source: 'MathVista',
            url: 'https://mathvista.github.io/',
            note: 'MathVista는 수학 도표, 차트, 문서 이미지 등 시각-수학 추론 예시를 제공한다.',
        },
    ],
    'korean-safety': [
        {
            title: 'Hunminjeongeum image',
            source: 'Wikimedia Commons',
            url: 'https://commons.wikimedia.org/wiki/File:Hunminjeongum.jpg',
            note: 'Wikimedia Commons의 한글 시각 자료는 한국어 평가가 별도 축이어야 하는 이유를 설명하는 보조 이미지로 사용한다.',
        },
        {
            title: 'Flag of South Korea',
            source: 'Wikimedia Commons',
            url: 'https://commons.wikimedia.org/wiki/File:Gyeonghoeru_(Royal_Banquet_Hall)_at_Gyeongbokgung_Palace,_Seoul.jpg',
            note: 'Wikimedia Commons의 대한민국 국기 이미지는 한국어·한국 문화권 평가축을 구분하는 보조 시각 자료로 사용한다.',
        },
    ],
};

export const BENCHMARK_CATEGORY_VISUALS: Record<string, BenchmarkVisual[]> = {
    reasoning: [
        {
            src: 'https://www.lastexam.ai/_next/static/media/difficulty_comparison_new.54376f68.png',
            alt: 'HLE와 기존 지식 벤치마크 난도 비교 도식',
            caption: '정답형 지식·추론 벤치마크는 기존 세트가 포화되면 난도를 높인 새 축이 필요해진다.',
            sourceTitle: 'Humanity\'s Last Exam official site',
            sourceUrl: 'https://www.lastexam.ai/',
            referenceIndex: 2,
            featured: true,
        },
        {
            src: 'https://raw.githubusercontent.com/centerforaisafety/hle/main/images/dataset.png',
            alt: 'HLE 데이터셋 분야 분포 도식',
            caption: '고난도 지식 벤치마크는 단일 과목이 아니라 여러 전문 분야를 나누어 설계한다.',
            sourceTitle: 'Humanity\'s Last Exam dataset image',
            sourceUrl: 'https://github.com/centerforaisafety/hle/blob/main/images/dataset.png',
            referenceIndex: 3,
        },
    ],
    coding: [
        {
            src: 'https://filecdn.minimax.chat/public/58eca777-e31f-448a-9823-e2220e49b426.png',
            alt: '저장소 이슈 해결형 코딩 벤치마크 도식',
            caption: '코딩 벤치마크는 단순 함수 생성에서 실제 저장소 이슈 해결과 테스트 통과로 확장된다.',
            sourceTitle: 'SWE-bench leaderboard raster image',
            sourceUrl: 'https://www.swebench.com/',
            referenceIndex: 2,
        },
        {
            src: 'https://www.tbench.ai/og/ascii-logo-dark-1200x630.png',
            alt: '터미널 작업형 코딩 벤치마크 도식',
            caption: '터미널 기반 평가는 파일, 셸, 서비스 실행, 테스트 로그까지 포함해 agent 작업성을 본다.',
            sourceTitle: 'Terminal-Bench raster image',
            sourceUrl: 'https://www.tbench.ai/',
            referenceIndex: 3,
            featured: true,
        },
    ],
    'agent-web': [
        {
            src: 'https://webarena.dev/static/images/webarena-logo.png',
            alt: '웹 에이전트 탐색 벤치마크 도식',
            caption: '웹 에이전트 벤치마크는 실제 웹 앱과 유사한 환경에서 탐색과 조작 성공률을 측정한다.',
            sourceTitle: 'WebArena logo image',
            sourceUrl: 'https://webarena.dev/',
            referenceIndex: 2,
        },
        {
            src: 'https://os-world.github.io/static/images/task_demonstration.png',
            alt: 'computer-use 작업 상태 변경 도식',
            caption: 'computer-use 평가는 브라우저 밖의 데스크톱 앱, 파일, 상태 변경까지 포함한다.',
            sourceTitle: 'OSWorld task demonstration image',
            sourceUrl: 'https://os-world.github.io/',
            referenceIndex: 3,
            featured: true,
        },
    ],
    'tool-use': [
        {
            src: 'https://gorilla.cs.berkeley.edu/assets/img/Cal.png',
            alt: 'function calling 평가 흐름 도식',
            caption: '툴 호출 평가는 함수 선택뿐 아니라 인자 구성, 병렬 호출, 호출 순서를 분리해 본다.',
            sourceTitle: 'Berkeley Function Calling Leaderboard image',
            sourceUrl: 'https://gorilla.cs.berkeley.edu/leaderboard.html',
            referenceIndex: 2,
            featured: true,
        },
        {
            src: 'https://mcpmark.ai/og.png',
            alt: 'MCP 도구 조합 평가 도식',
            caption: 'MCP 계열 평가는 실제 도구 발견과 여러 도구 조합을 스트레스 테스트하는 방향으로 확장된다.',
            sourceTitle: 'MCPMark raster image',
            sourceUrl: 'https://mcpmark.ai/',
            referenceIndex: 3,
        },
    ],
    multimodal: [
        {
            src: 'https://mmmu-benchmark.github.io/static/images/statistics.png',
            alt: '멀티모달 분야와 이미지 유형 분포 도식',
            caption: '멀티모달 벤치마크는 문제 분야, 이미지 유형, 전문 지식 난도를 함께 나누어 봐야 한다.',
            sourceTitle: 'MMMU statistics image',
            sourceUrl: 'https://mmmu-benchmark.github.io/',
            referenceIndex: 2,
            featured: true,
        },
        {
            src: 'https://mathvista.github.io/static/images/contexts/3_Bar_Chart.png',
            alt: '차트 기반 수학 문항 도식',
            caption: '차트, 도표, 문서 이미지를 함께 읽는 문제는 텍스트-only 벤치마크와 실패 유형이 다르다.',
            sourceTitle: 'MathVista chart image',
            sourceUrl: 'https://mathvista.github.io/',
            referenceIndex: 3,
        },
    ],
    'korean-safety': [
        {
            src: 'https://upload.wikimedia.org/wikipedia/commons/c/c9/Hunminjeongum.jpg',
            alt: '한국어 언어 구조와 문화 맥락 도식',
            caption: '한국어 평가에서는 번역된 영어 문항만으로는 언어 구조와 문화 맥락을 충분히 설명하기 어렵다.',
            sourceTitle: 'Hunminjeongeum image',
            sourceUrl: 'https://commons.wikimedia.org/wiki/File:Hunminjeongum.jpg',
            referenceIndex: 2,
        },
        {
            src: 'https://upload.wikimedia.org/wikipedia/commons/5/5d/Gyeonghoeru_%28Royal_Banquet_Hall%29_at_Gyeongbokgung_Palace%2C_Seoul.jpg',
            alt: '한국어 지식과 안전 평가축 도식',
            caption: '한국어 지식·안전 벤치마크는 한국 제도, 문화, 사회적 맥락을 별도 평가축으로 둔다.',
            sourceTitle: 'Gyeongbokgung Palace image',
            sourceUrl: 'https://commons.wikimedia.org/wiki/File:Gyeonghoeru_(Royal_Banquet_Hall)_at_Gyeongbokgung_Palace,_Seoul.jpg',
            referenceIndex: 3,
            featured: true,
        },
    ],
};

export const BENCHMARK_WIKI_ROOT = '성능·검증 > 벤치마크';

export const BENCHMARK_TOC_ITEMS = BENCHMARKS.filter((benchmark) => !benchmark.sourceUrl.startsWith('https://github.com/'));

export const FEATURED_BENCHMARK_SLUGS = [
    'gpqa-diamond',
    'swe-bench-verified',
    'terminal-bench-2',
    'browsecomp',
    'bfcl',
    'tau-bench',
    'mmmu',
    'kmmlu',
    'harmbench',
];

export const FEATURED_BENCHMARKS = FEATURED_BENCHMARK_SLUGS
    .map((slug) => BENCHMARK_TOC_ITEMS.find((benchmark) => benchmark.slug === slug))
    .filter((benchmark): benchmark is BenchmarkDetail => !!benchmark);
